import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { axiosPrivate } from "../../../api/axios";
import API_URL from "../../../utils/API_URL";

const DEFAULT_SIM_ID = 1;
const MAX_CHANGE_PREVIEW_ITEMS = 120;
const MAX_EXECUTION_LOG_LINES = 300;
const FIXED_LAYOUT_ID = "sim_data04";
const PROD_PLAN_TABLE = "sim_prod_plan";
const ITEM_MASTER_TABLE = "sim_item_order_master";

const DOMAIN_DEFINITIONS = [
  {
    id: "plan",
    label: "생산계획",
    description: "생산 수량/시작 조건/납기 계획 설정",
  },
  {
    id: "logistics",
    label: "물류",
    description: "반송장치 이동/적재/로딩 관련 설정",
  },
  {
    id: "machine",
    label: "장비",
    description: "장비 성능/고장/버퍼 운용 설정",
  },
  {
    id: "worker",
    label: "작업자",
    description: "작업자 지연, 복귀, 이동 속도 설정",
  },
  {
    id: "process",
    label: "공정",
    description: "공정 시간, 가용 장비, 공정간 비율 설정",
  },
  {
    id: "lot",
    label: "Lot",
    description: "Lot 수량/칸반 및 제품 변수 관련 설정",
  },
  {
    id: "buffer",
    label: "Buffer",
    description: "초기 버퍼 재공 수량 및 위치 설정",
  },
  {
    id: "execute",
    label: "실행/결과",
    description: "실행 규칙 및 결과 출력 대상 설정",
  },
  {
    id: "reference",
    label: "참조",
    description: "읽기 전용 참조 데이터",
  },
];

const TABLE_DOMAIN_MAP = {
  sim_carrier_master: "logistics",
  sim_carrier_teaching: "logistics",
  sim_machine_master: "machine",
  sim_mc_usable_proc: "machine",
  sim_worker_master: "worker",
  sim_proc_master: "process",
  sim_prm_res_proc_leadtime: "process",
  sim_lot_master: "lot",
  sim_item_variable: "lot",
  sim_item_variable_order: "lot",
  sim_prod_plan: "plan",
  sim_item_order_master: "plan",
  sim_prod_buffer_work: "buffer",
  sim_exe_info: "execute",
  sim_select_result: "execute",
  sim_parameter: "execute",
};

const TABLE_GUIDE = {
  sim_carrier_master: {
    headline: "물류 기본 성능",
    relationHint:
      "carrier_id 기준으로 물류별 고장률, 속도, 적재능력을 조정합니다.",
    coreFields: [
      "down_rate",
      "restore_time",
      "move_speed",
      "lot_size",
      "carrier_cnt",
      "loading",
      "unloading",
    ],
    rowTitleFields: ["carrier_name", "carrier_id"],
  },
  sim_carrier_teaching: {
    headline: "물류 로딩/언로딩 동작",
    relationHint:
      "carrier_id + mch_id + carr_stat 조합별 작업시간을 설정합니다.",
    coreFields: ["carr_stat", "teaching_id", "loading_time"],
    rowTitleFields: ["carrier_id", "mch_id", "carr_stat"],
  },
  sim_machine_master: {
    headline: "장비 성능과 버퍼 전략",
    relationHint:
      "divide_buffer 값에 따라 통합 버퍼(buffer_cnt) 또는 분리 버퍼(in/out)를 사용합니다.",
    coreFields: [
      "down_rate",
      "restore_time",
      "defect_rate",
      "ability_rate",
      "divide_buffer",
      "buffer_cnt",
      "in_buffer_cnt",
      "out_buffer_cnt",
    ],
    rowTitleFields: ["mch_name", "mch_id"],
  },
  sim_worker_master: {
    headline: "작업자 수와 지연 특성",
    relationHint:
      "worker_id 단위로 작업자 지연률/복귀시간/이동속도를 조정합니다.",
    coreFields: ["down_rate", "restore_time", "move_speed", "worker_cnt"],
    rowTitleFields: ["worker_name", "worker_id"],
  },
  sim_proc_master: {
    headline: "공정 표준 시간",
    relationHint:
      "worker_enable이 1인 공정만 worker_time이 실질적으로 사용됩니다.",
    coreFields: ["proc_time", "worker_time"],
    rowTitleFields: ["proc_name", "proc_id"],
  },
  sim_mc_usable_proc: {
    headline: "장비-공정 가용 매핑",
    relationHint:
      "mch_id/item_id/proc_id 조합별로 장비별 공정시간 사용 여부를 설정합니다.",
    coreFields: ["enable_time", "proc_time", "worker_time"],
    rowTitleFields: ["mch_id", "item_id", "proc_id"],
  },
  sim_lot_master: {
    headline: "Lot 생산/투입 수량",
    relationHint:
      "Lot별 생성 수량(from_lot_qty), 투입 수량(to_lot_qty), Kanban 수량을 조정합니다.",
    coreFields: ["from_lot_qty", "to_lot_qty", "kanban"],
    rowTitleFields: ["lot_name", "lot_id"],
  },
  sim_item_variable: {
    headline: "Item 변수 기준값",
    relationHint:
      "읽기 전용 참조 테이블입니다. 실제 계획별 값은 생산계획 변수 값에서 조정합니다.",
    coreFields: [],
    rowTitleFields: ["variable_name", "item_id", "variable_id"],
  },
  sim_item_variable_order: {
    headline: "생산계획별 Item 변수 값",
    relationHint: "prod_id/item_id/variable_id 조합별 value를 조정합니다.",
    coreFields: ["value"],
    rowTitleFields: ["prod_id", "item_id", "variable_id"],
  },
  sim_prod_plan: {
    headline: "생산계획 시작 조건",
    relationHint:
      "status는 0:생산, 1:skip이며, start_type=1이면 지정한 start_order_id 주문이 start_count 생산량에 도달한 뒤 시작됩니다.",
    coreFields: [
      "prod_name",
      "item_id",
      "quantity",
      "status",
      "start_type",
      "start_time",
      "due_time",
      "start_order_id",
      "start_lot_id",
      "start_count",
    ],
    rowTitleFields: ["prod_name", "item_name", "quantity"],
  },
  sim_item_order_master: {
    headline: "제품/LOT 참조 정보",
    relationHint:
      "읽기 전용 참조 테이블입니다. 생산계획 수립 시 대상 item/lot 확인에 사용됩니다.",
    coreFields: [],
    rowTitleFields: ["item_name", "lot_name", "item_id"],
  },
  sim_prod_buffer_work: {
    headline: "초기 버퍼 재공 설정",
    relationHint:
      "mch_id/pos_type/prod_id/lot_id 조합별 초기 quantity를 조정합니다.",
    coreFields: ["quantity"],
    rowTitleFields: ["mch_id", "pos_type", "prod_id", "lot_id"],
  },
  sim_exe_info: {
    headline: "실행 규칙",
    relationHint: "dispatching_rule은 -1(미사용) 또는 0~12 범위를 사용합니다.",
    coreFields: [
      "dispatching_rule1",
      "dispatching_rule2",
      "dispatching_rule3",
      "start_sim_time",
    ],
    rowTitleFields: ["start_sim_time"],
  },
  sim_select_result: {
    headline: "결과 출력 선택",
    relationHint:
      "use_flag를 1로 설정한 결과 테이블만 출력 대상으로 사용합니다.",
    coreFields: ["use_flag"],
    rowTitleFields: ["table_desc", "table_name"],
  },
  sim_parameter: {
    headline: "실행 메타 파라미터",
    relationHint: "시뮬레이션 실행 시 key_name별 value_data를 관리합니다.",
    coreFields: ["value_data"],
    rowTitleFields: ["key_name"],
  },
  sim_prm_res_proc_leadtime: {
    headline: "공정간 LeadTime 비율",
    relationHint:
      "proc_id1/proc_id2 및 proc_rate1/proc_rate2로 공정간 비율을 조정합니다.",
    coreFields: [
      "proc_leadtime_name",
      "order_id1",
      "proc_id1",
      "proc_rate1",
      "order_id2",
      "proc_id2",
      "proc_rate2",
    ],
    rowTitleFields: ["proc_leadtime_name", "proc_leadtime_no"],
  },
};

const DISPATCHING_RULE_OPTIONS = [
  { value: "-1", label: "없음(-1)" },
  ...Array.from({ length: 13 }, (_, idx) => ({
    value: String(idx),
    label: `Rule ${idx}`,
  })),
];

const FIELD_OPTION_MAP = {
  divide_buffer: [
    { value: "0", label: "통합 Buffer" },
    { value: "1", label: "분리 Buffer" },
  ],
  start_type: [
    { value: "0", label: "시작일시(start_time) 기준" },
    { value: "1", label: "생산 조건(start_order_id) 기준" },
  ],
  enable_time: [
    { value: "0", label: "장비별 공정시간 미사용" },
    { value: "1", label: "장비별 공정시간 사용" },
  ],
  use_flag: [
    { value: "0", label: "결과 비사용" },
    { value: "1", label: "결과 사용" },
  ],
  status: [
    { value: "0", label: "생산" },
    { value: "1", label: "skip" },
  ],
  carr_stat: [
    { value: "3", label: "로딩" },
    { value: "5", label: "언로딩" },
  ],
};

const isNumericType = (columnType = "") => {
  const lowerType = columnType.toLowerCase();
  return (
    lowerType.includes("int") ||
    lowerType.includes("float") ||
    lowerType.includes("double") ||
    lowerType.includes("decimal")
  );
};

const isIntegerType = (columnType = "") =>
  columnType.toLowerCase().includes("int");

const isDateType = (columnType = "") => {
  const lowerType = columnType.toLowerCase();
  return (
    lowerType.includes("datetime") ||
    lowerType.includes("timestamp") ||
    lowerType === "date"
  );
};

const toComparableValue = (value) => {
  if (value === undefined || value === null) {
    return "";
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString();
    }
  }

  return String(value);
};

const toDateTimeLocalInput = (value) => {
  if (value === undefined || value === null || value === "") {
    return "";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  const offsetInMs = parsed.getTimezoneOffset() * 60000;
  return new Date(parsed.getTime() - offsetInMs).toISOString().slice(0, 16);
};

const normalizeDateInput = (value) => {
  if (!value) {
    return "";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toISOString();
};

const normalizeText = (value) => String(value ?? "").toLowerCase();

const normalizeIdKey = (value) => String(value ?? "").trim();

const hasValue = (value) => {
  if (value === undefined || value === null) {
    return false;
  }
  if (typeof value === "string") {
    return value.trim() !== "";
  }
  return true;
};

const parseDateSafe = (value) => {
  if (!hasValue(value)) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
};

const parseIntegerSafe = (value) => {
  const parsed = Number(value);
  if (!Number.isInteger(parsed)) {
    return null;
  }
  return parsed;
};

const isNewLocalRow = (row) => {
  const rowKey = row?.__rowKey;
  return (
    row?.__isNew === true ||
    (typeof rowKey === "string" && rowKey.startsWith("__new__"))
  );
};

const parseOptionsFromDescription = (description = "") => {
  if (!description.includes(":")) {
    return [];
  }

  const matches = Array.from(description.matchAll(/(-?\d+)\s*:\s*([^,/)]+)/g));
  if (matches.length === 0) {
    return [];
  }

  const options = [];
  const seen = new Set();
  matches.forEach((match) => {
    const value = String(Number(match[1]));
    if (seen.has(value)) {
      return;
    }

    seen.add(value);
    options.push({
      value,
      label: match[2].trim(),
    });
  });

  return options;
};

const getFieldOptions = (column) => {
  if (!column) {
    return [];
  }

  if (column.name.startsWith("dispatching_rule")) {
    return DISPATCHING_RULE_OPTIONS;
  }

  if (FIELD_OPTION_MAP[column.name]) {
    return FIELD_OPTION_MAP[column.name];
  }

  const parsed = parseOptionsFromDescription(column.description || "");
  return parsed.length >= 2 ? parsed : [];
};

const getFieldLabel = (column) => {
  if (!column) {
    return "";
  }

  if (!column.description) {
    return column.name;
  }

  const cleanDescription = column.description.split("(")[0].trim();
  if (cleanDescription.length > 0) {
    return cleanDescription;
  }

  return column.name;
};

const getUnitText = (description = "") => {
  const matches = description.match(/\(([^()]+)\)/g);
  if (!matches || matches.length === 0) {
    return "";
  }

  return matches[matches.length - 1].replace(/[()]/g, "").trim();
};

const inferDomainIdByTable = (tableName, tableDesc = "") => {
  if (TABLE_DOMAIN_MAP[tableName]) {
    return TABLE_DOMAIN_MAP[tableName];
  }

  const lower = `${tableName} ${tableDesc}`.toLowerCase();

  if (lower.includes("carrier") || lower.includes("물류")) {
    return "logistics";
  }

  if (
    lower.includes("machine") ||
    lower.includes("mch") ||
    lower.includes("장비")
  ) {
    return "machine";
  }

  if (lower.includes("worker") || lower.includes("작업자")) {
    return "worker";
  }

  if (lower.includes("proc") || lower.includes("공정")) {
    return "process";
  }

  if (
    lower.includes("lot") ||
    lower.includes("item_variable") ||
    lower.includes("lot")
  ) {
    return "lot";
  }

  if (
    lower.includes("prod") ||
    lower.includes("plan") ||
    lower.includes("생산")
  ) {
    return "plan";
  }

  if (lower.includes("buffer")) {
    return "buffer";
  }

  if (
    lower.includes("sim_exe") ||
    lower.includes("sim_parameter") ||
    lower.includes("result")
  ) {
    return "execute";
  }

  return "reference";
};

const makeRowKey = (row, tableName, rowIndex) =>
  row.__rowKey || `${tableName}::${rowIndex}`;

const buildRowTitle = (tableMeta, row, rowIndex) => {
  if (!tableMeta || !row) {
    return `행 ${rowIndex + 1}`;
  }

  const guide = TABLE_GUIDE[tableMeta.tableName];
  const preferredFields = guide?.rowTitleFields || [];
  const preferredParts = preferredFields
    .map((fieldName) => row[fieldName])
    .filter(
      (value) =>
        value !== undefined && value !== null && String(value).trim() !== "",
    )
    .map((value) => String(value));

  if (preferredParts.length > 0) {
    return preferredParts.join(" / ");
  }

  const primaryKeyParts = (tableMeta.primaryKeys || [])
    .map((primaryKey) => row[primaryKey])
    .filter((value) => value !== undefined && value !== null)
    .map((value) => String(value));

  if (primaryKeyParts.length > 0) {
    return primaryKeyParts.join(" / ");
  }

  return `행 ${rowIndex + 1}`;
};

const shouldShowFieldByDependency = (tableName, row, columnName) => {
  if (!row) {
    return true;
  }

  if (tableName === "sim_machine_master") {
    const divideBuffer = Number(row.divide_buffer ?? 0);
    if (
      divideBuffer === 0 &&
      (columnName === "in_buffer_cnt" || columnName === "out_buffer_cnt")
    ) {
      return false;
    }

    if (divideBuffer === 1 && columnName === "buffer_cnt") {
      return false;
    }
  }

  if (tableName === "sim_prod_plan") {
    const startType = Number(row.start_type ?? 0);
    if (
      startType === 0 &&
      ["start_order_id", "start_lot_id", "start_count"].includes(columnName)
    ) {
      return false;
    }

    if (startType === 1 && columnName === "start_time") {
      return false;
    }
  }

  if (tableName === "sim_proc_master") {
    const workerEnable = Number(row.worker_enable ?? 0);
    if (workerEnable === 0 && columnName === "worker_time") {
      return false;
    }
  }

  return true;
};

const getDependencyHint = (tableName, row) => {
  if (!row) {
    return "";
  }

  if (tableName === "sim_machine_master") {
    const divideBuffer = Number(row.divide_buffer ?? 0);
    return divideBuffer === 1
      ? "분리 Buffer 모드입니다. in_buffer_cnt / out_buffer_cnt를 조정하세요."
      : "통합 Buffer 모드입니다. buffer_cnt를 조정하세요.";
  }

  if (tableName === "sim_prod_plan") {
    const startType = Number(row.start_type ?? 0);
    return startType === 1
      ? "생산 조건 기반 시작입니다. start_order_id로 지정한 주문(prod_id)이 start_count 생산량에 도달한 뒤 이 주문을 시작합니다."
      : "시작일시 기반 시작입니다. start_time이 사용됩니다.";
  }

  if (tableName === "sim_proc_master") {
    const workerEnable = Number(row.worker_enable ?? 0);
    return workerEnable === 1
      ? "작업자 투입 공정입니다. worker_time이 함께 반영됩니다."
      : "작업자 비투입 공정입니다. worker_time은 시뮬레이션에 사용되지 않습니다.";
  }

  return "";
};

const hasCancelKeyword = (text = "") => {
  const normalizedText = String(text).toLowerCase();
  return ["cancel", "cancelled", "canceled", "취소", "중단"].some((keyword) =>
    normalizedText.includes(keyword),
  );
};

const parseSimulationStatusCode = (payload = {}) => {
  const rawStatus = payload?.Status ?? payload?.status;
  const parsedStatus = Number(rawStatus);
  if (!Number.isInteger(parsedStatus)) {
    return null;
  }
  return parsedStatus;
};

export default function SimulationParameterPopup({
  open,
  onClose,
  groupCode,
  projectCode,
  onExecuteRequested,
}) {
  const rootUrl = useMemo(() => API_URL.replace("/backend", ""), []);
  const domainLabelMap = useMemo(
    () =>
      DOMAIN_DEFINITIONS.reduce((acc, domain) => {
        acc[domain.id] = domain.label;
        return acc;
      }, {}),
    [],
  );

  const [schemaTables, setSchemaTables] = useState([]);
  const [selectedDomainId, setSelectedDomainId] = useState("");
  const [selectedTableName, setSelectedTableName] = useState("");
  const [selectedRowKeyByTable, setSelectedRowKeyByTable] = useState({});

  const [tableRowsMap, setTableRowsMap] = useState({});
  const [initialRowsMap, setInitialRowsMap] = useState({});
  const [pendingDeleteRowsByTable, setPendingDeleteRowsByTable] = useState({});

  const [tableSearchKeyword, setTableSearchKeyword] = useState("");
  const [rowSearchKeyword, setRowSearchKeyword] = useState("");
  const [simId, setSimId] = useState(DEFAULT_SIM_ID);

  const [viewMode, setViewMode] = useState("guided");
  const [showAdvancedFields, setShowAdvancedFields] = useState(false);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [executionStatus, setExecutionStatus] = useState("idle");
  const [executionStatusMessage, setExecutionStatusMessage] = useState("");
  const [executionLogs, setExecutionLogs] = useState([]);
  const [isExecutionLogAutoFollow, setIsExecutionLogAutoFollow] =
    useState(true);

  const [schemaLoading, setSchemaLoading] = useState(false);
  const [loadingTableName, setLoadingTableName] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const eventSourceRef = useRef(null);
  const manualStopRef = useRef(false);
  const completionDetectedRef = useRef(false);
  const executionLogContainerRef = useRef(null);
  const rowListContainerRef = useRef(null);
  const rowOptionButtonRefMap = useRef(new Map());
  const isExecutionInProgress = ["starting", "running", "cancelling"].includes(
    executionStatus,
  );

  const activeLayoutId = FIXED_LAYOUT_ID;

  const selectedTableMeta = useMemo(
    () =>
      schemaTables.find((table) => table.tableName === selectedTableName) ||
      null,
    [schemaTables, selectedTableName],
  );

  const domainGroups = useMemo(() => {
    const grouped = DOMAIN_DEFINITIONS.reduce((acc, domain) => {
      acc[domain.id] = {
        ...domain,
        tables: [],
      };
      return acc;
    }, {});

    schemaTables.forEach((table) => {
      const domainId = inferDomainIdByTable(table.tableName, table.tableDesc);
      if (!grouped[domainId]) {
        grouped[domainId] = {
          id: domainId,
          label: domainLabelMap[domainId] || "기타",
          description: "",
          tables: [],
        };
      }
      grouped[domainId].tables.push(table);
    });

    return Object.values(grouped).filter((domain) => domain.tables.length > 0);
  }, [domainLabelMap, schemaTables]);

  const selectedDomain = useMemo(
    () => domainGroups.find((domain) => domain.id === selectedDomainId) || null,
    [domainGroups, selectedDomainId],
  );

  const selectedDomainTables = useMemo(() => {
    if (!selectedDomain) {
      return [];
    }

    const keyword = normalizeText(tableSearchKeyword).trim();
    if (!keyword) {
      return selectedDomain.tables;
    }

    return selectedDomain.tables.filter((table) => {
      const targetText = `${table.tableName} ${table.tableDesc || ""}`;
      return normalizeText(targetText).includes(keyword);
    });
  }, [selectedDomain, tableSearchKeyword]);

  const itemOrderMasterRows = useMemo(
    () => tableRowsMap[ITEM_MASTER_TABLE] || [],
    [tableRowsMap],
  );

  const itemOptions = useMemo(() => {
    const dedupe = new Map();

    itemOrderMasterRows.forEach((row) => {
      const itemIdKey = normalizeIdKey(row.item_id);
      if (!itemIdKey || dedupe.has(itemIdKey)) {
        return;
      }

      dedupe.set(itemIdKey, {
        itemId: row.item_id,
        itemName: row.item_name || "",
        lotId: row.lot_id,
        lotName: row.lot_name || "",
      });
    });

    return Array.from(dedupe.values());
  }, [itemOrderMasterRows]);

  const itemNameById = useMemo(() => {
    const mapper = new Map();
    itemOptions.forEach((option) => {
      mapper.set(normalizeIdKey(option.itemId), option.itemName || "");
    });
    return mapper;
  }, [itemOptions]);

  const selectedRows = useMemo(
    () => tableRowsMap[selectedTableName] || [],
    [tableRowsMap, selectedTableName],
  );

  const selectedInitialRows = useMemo(
    () => initialRowsMap[selectedTableName] || [],
    [initialRowsMap, selectedTableName],
  );

  const selectedRowKey = selectedRowKeyByTable[selectedTableName] || "";

  const selectedRowIndex = useMemo(() => {
    if (selectedRows.length === 0) {
      return -1;
    }

    if (!selectedRowKey) {
      return 0;
    }

    const index = selectedRows.findIndex(
      (row, rowIndex) =>
        makeRowKey(row, selectedTableName, rowIndex) === selectedRowKey,
    );

    return index >= 0 ? index : 0;
  }, [selectedRowKey, selectedRows, selectedTableName]);

  const selectedRow =
    selectedRowIndex >= 0 ? selectedRows[selectedRowIndex] : null;
  const selectedInitialRow = useMemo(() => {
    if (!selectedTableName || selectedRowIndex < 0 || !selectedRow) {
      return {};
    }

    const selectedRowKey = makeRowKey(
      selectedRow,
      selectedTableName,
      selectedRowIndex,
    );
    const initialRows = initialRowsMap[selectedTableName] || [];
    const found = initialRows.find(
      (row, rowIndex) =>
        makeRowKey(row, selectedTableName, rowIndex) === selectedRowKey,
    );

    return found || {};
  }, [initialRowsMap, selectedRow, selectedRowIndex, selectedTableName]);

  const getInitialRowMapByTable = useCallback(
    (tableName) => {
      const rows = initialRowsMap[tableName] || [];
      const mapper = new Map();

      rows.forEach((row, rowIndex) => {
        mapper.set(makeRowKey(row, tableName, rowIndex), row);
      });

      return mapper;
    },
    [initialRowsMap],
  );

  const getChangedCellCount = useCallback(
    (tableMeta, tableRows, initialRows, deletedRows = []) => {
      if (
        !tableMeta ||
        !Array.isArray(tableRows) ||
        !Array.isArray(initialRows)
      ) {
        return 0;
      }

      const initialRowMap = new Map();
      initialRows.forEach((row, rowIndex) => {
        initialRowMap.set(makeRowKey(row, tableMeta.tableName, rowIndex), row);
      });

      let count = 0;
      tableRows.forEach((row, rowIndex) => {
        const rowKey = makeRowKey(row, tableMeta.tableName, rowIndex);
        const initialRow = initialRowMap.get(rowKey) || {};

        tableMeta.columns.forEach((column) => {
          if (!column.editable) {
            return;
          }

          const currentValue = toComparableValue(row[column.name]);
          const beforeValue = isNewLocalRow(row)
            ? toComparableValue(column.defaultValue)
            : toComparableValue(initialRow[column.name]);

          if (currentValue !== beforeValue) {
            count += 1;
          }
        });
      });

      count += Array.isArray(deletedRows) ? deletedRows.length : 0;

      return count;
    },
    [],
  );

  const getChangedCellCountByRow = useCallback(
    (tableMeta, row, rowIndex) => {
      if (!tableMeta || !row) {
        return 0;
      }

      const rowKey = makeRowKey(row, tableMeta.tableName, rowIndex);
      const initialRowMap = getInitialRowMapByTable(tableMeta.tableName);
      const initialRow = initialRowMap.get(rowKey) || {};

      let count = 0;
      tableMeta.columns.forEach((column) => {
        if (!column.editable) {
          return;
        }

        const currentValue = toComparableValue(row[column.name]);
        const beforeValue = isNewLocalRow(row)
          ? toComparableValue(column.defaultValue)
          : toComparableValue(initialRow[column.name]);

        if (currentValue !== beforeValue) {
          count += 1;
        }
      });

      return count;
    },
    [getInitialRowMapByTable],
  );

  const changedCellCountByTable = useMemo(() => {
    const changedMap = {};
    schemaTables.forEach((tableMeta) => {
      const tableRows = tableRowsMap[tableMeta.tableName] || [];
      const initialRows = initialRowsMap[tableMeta.tableName] || [];
      const deletedRows = pendingDeleteRowsByTable[tableMeta.tableName] || [];
      changedMap[tableMeta.tableName] = getChangedCellCount(
        tableMeta,
        tableRows,
        initialRows,
        deletedRows,
      );
    });
    return changedMap;
  }, [
    getChangedCellCount,
    initialRowsMap,
    pendingDeleteRowsByTable,
    schemaTables,
    tableRowsMap,
  ]);

  const changedCellCountByDomain = useMemo(() => {
    const domainMap = {};
    domainGroups.forEach((domain) => {
      domainMap[domain.id] = domain.tables.reduce(
        (acc, table) => acc + (changedCellCountByTable[table.tableName] || 0),
        0,
      );
    });
    return domainMap;
  }, [changedCellCountByTable, domainGroups]);

  const totalChangedCells = useMemo(
    () =>
      Object.values(changedCellCountByTable).reduce(
        (acc, current) => acc + current,
        0,
      ),
    [changedCellCountByTable],
  );

  const totalChangedTables = useMemo(
    () =>
      schemaTables.filter(
        (tableMeta) => (changedCellCountByTable[tableMeta.tableName] || 0) > 0,
      ).length,
    [changedCellCountByTable, schemaTables],
  );

  const filteredRows = useMemo(() => {
    if (!selectedTableMeta) {
      return [];
    }

    const keyword = normalizeText(rowSearchKeyword).trim();
    const entries = selectedRows.map((row, rowIndex) => ({ row, rowIndex }));

    if (!keyword) {
      return entries;
    }

    return entries.filter(({ row, rowIndex }) => {
      const rowTitle = buildRowTitle(selectedTableMeta, row, rowIndex);
      if (normalizeText(rowTitle).includes(keyword)) {
        return true;
      }

      return selectedTableMeta.columns.some((column) =>
        normalizeText(row[column.name]).includes(keyword),
      );
    });
  }, [rowSearchKeyword, selectedRows, selectedTableMeta]);

  const tableGuide = selectedTableMeta
    ? TABLE_GUIDE[selectedTableMeta.tableName]
    : null;

  const visibleEditableColumns = useMemo(() => {
    if (!selectedTableMeta) {
      return [];
    }

    return selectedTableMeta.columns.filter(
      (column) =>
        column.editable &&
        shouldShowFieldByDependency(
          selectedTableMeta.tableName,
          selectedRow,
          column.name,
        ),
    );
  }, [selectedRow, selectedTableMeta]);

  const { coreEditableColumns, advancedEditableColumns } = useMemo(() => {
    if (!selectedTableMeta) {
      return { coreEditableColumns: [], advancedEditableColumns: [] };
    }

    const preferredCore = tableGuide?.coreFields || [];
    const availableColumnNameSet = new Set(
      visibleEditableColumns.map((column) => column.name),
    );

    const coreFieldNameSet = new Set(
      preferredCore.length > 0
        ? preferredCore.filter((columnName) =>
            availableColumnNameSet.has(columnName),
          )
        : visibleEditableColumns.slice(0, 4).map((column) => column.name),
    );

    const coreColumns = visibleEditableColumns.filter((column) =>
      coreFieldNameSet.has(column.name),
    );
    const advancedColumns = visibleEditableColumns.filter(
      (column) => !coreFieldNameSet.has(column.name),
    );

    return {
      coreEditableColumns: coreColumns,
      advancedEditableColumns: advancedColumns,
    };
  }, [selectedTableMeta, tableGuide, visibleEditableColumns]);

  const visibleReadonlyColumns = useMemo(() => {
    if (!selectedTableMeta) {
      return [];
    }

    return selectedTableMeta.columns.filter((column) => !column.editable);
  }, [selectedTableMeta]);

  const dependencyHint = useMemo(
    () => getDependencyHint(selectedTableMeta?.tableName, selectedRow),
    [selectedRow, selectedTableMeta],
  );

  const setRowOptionButtonRef = useCallback((rowKey, node) => {
    if (!rowKey) {
      return;
    }

    if (node) {
      rowOptionButtonRefMap.current.set(rowKey, node);
      return;
    }

    rowOptionButtonRefMap.current.delete(rowKey);
  }, []);

  const changePreviewItems = useMemo(() => {
    const items = [];

    schemaTables.forEach((tableMeta) => {
      const tableRows = tableRowsMap[tableMeta.tableName] || [];
      const initialRowMap = getInitialRowMapByTable(tableMeta.tableName);
      const deletedRows = pendingDeleteRowsByTable[tableMeta.tableName] || [];
      const domainId = inferDomainIdByTable(
        tableMeta.tableName,
        tableMeta.tableDesc,
      );
      const domainLabel = domainLabelMap[domainId] || "기타";

      tableRows.forEach((row, rowIndex) => {
        const rowKey = makeRowKey(row, tableMeta.tableName, rowIndex);
        const initialRow = initialRowMap.get(rowKey) || {};

        tableMeta.columns.forEach((column) => {
          if (!column.editable) {
            return;
          }

          const currentValue = toComparableValue(row[column.name]);
          const beforeValue = isNewLocalRow(row)
            ? toComparableValue(column.defaultValue)
            : toComparableValue(initialRow[column.name]);

          if (currentValue === beforeValue) {
            return;
          }

          items.push({
            domainId,
            domainLabel,
            tableName: tableMeta.tableName,
            tableDesc: tableMeta.tableDesc,
            rowIndex,
            rowLabel: buildRowTitle(tableMeta, row, rowIndex),
            columnName: column.name,
            columnLabel: getFieldLabel(column),
            beforeValue,
            currentValue,
          });
        });
      });

      deletedRows.forEach((row) => {
        const rowLabel = buildRowTitle(tableMeta, row, 0);
        items.push({
          domainId,
          domainLabel,
          tableName: tableMeta.tableName,
          tableDesc: tableMeta.tableDesc,
          rowIndex: 0,
          rowLabel,
          columnName: "__row_delete__",
          columnLabel: "행 삭제",
          beforeValue: rowLabel,
          currentValue: "삭제",
        });
      });
    });

    return items;
  }, [
    domainLabelMap,
    getInitialRowMapByTable,
    pendingDeleteRowsByTable,
    schemaTables,
    tableRowsMap,
  ]);

  const reviewItems = useMemo(
    () => changePreviewItems.slice(0, MAX_CHANGE_PREVIEW_ITEMS),
    [changePreviewItems],
  );

  const omittedReviewItemCount = Math.max(
    changePreviewItems.length - reviewItems.length,
    0,
  );

  const reviewByDomain = useMemo(() => {
    return domainGroups
      .map((domain) => {
        const items = reviewItems.filter((item) => item.domainId === domain.id);
        return {
          id: domain.id,
          label: domain.label,
          items,
        };
      })
      .filter((domain) => domain.items.length > 0);
  }, [domainGroups, reviewItems]);

  const buildChangesPayload = useCallback(() => {
    const changes = [];

    schemaTables.forEach((tableMeta) => {
      const currentRows = tableRowsMap[tableMeta.tableName];
      const initialRows = initialRowsMap[tableMeta.tableName];
      const deletedRows = pendingDeleteRowsByTable[tableMeta.tableName] || [];

      if (!Array.isArray(currentRows) || !Array.isArray(initialRows)) {
        return;
      }

      const initialRowMap = new Map();
      initialRows.forEach((row, rowIndex) => {
        initialRowMap.set(makeRowKey(row, tableMeta.tableName, rowIndex), row);
      });

      const rowChanges = [];

      currentRows.forEach((row, rowIndex) => {
        if (isNewLocalRow(row)) {
          const valuesPayload = {};
          tableMeta.columns.forEach((column) => {
            if (!column.editable) {
              return;
            }
            if (column.name === "item_name") {
              return;
            }
            valuesPayload[column.name] = row[column.name] ?? null;
          });

          rowChanges.push({
            action: "insert",
            values: valuesPayload,
          });
          return;
        }

        const rowKey = makeRowKey(row, tableMeta.tableName, rowIndex);
        const initialRow = initialRowMap.get(rowKey) || {};
        const setPayload = {};

        tableMeta.columns.forEach((column) => {
          if (!column.editable) {
            return;
          }
          if (column.name === "item_name") {
            return;
          }

          const currentValue = toComparableValue(row[column.name]);
          const beforeValue = toComparableValue(initialRow[column.name]);

          if (currentValue !== beforeValue) {
            setPayload[column.name] = row[column.name];
          }
        });

        if (Object.keys(setPayload).length === 0) {
          return;
        }

        if (tableMeta.primaryKeys.length === 0) {
          rowChanges.push({
            key: "__singleton__",
            set: setPayload,
          });
          return;
        }

        const keyPayload = {};
        tableMeta.primaryKeys.forEach((primaryKey) => {
          keyPayload[primaryKey] = initialRow[primaryKey];
        });

        rowChanges.push({
          key: keyPayload,
          set: setPayload,
        });
      });

      deletedRows.forEach((row) => {
        if (tableMeta.primaryKeys.length === 0) {
          return;
        }

        const keyPayload = {};
        tableMeta.primaryKeys.forEach((primaryKey) => {
          keyPayload[primaryKey] = row[primaryKey];
        });

        rowChanges.push({
          action: "delete",
          key: keyPayload,
        });
      });

      if (rowChanges.length > 0) {
        changes.push({
          table: tableMeta.tableName,
          rows: rowChanges,
        });
      }
    });

    return changes;
  }, [initialRowsMap, pendingDeleteRowsByTable, schemaTables, tableRowsMap]);

  const validateChanges = useCallback(() => {
    const errors = [];

    schemaTables.forEach((tableMeta) => {
      if (
        !Array.isArray(tableMeta.primaryKeys) ||
        tableMeta.primaryKeys.length === 0
      ) {
        return;
      }

      const rows = tableRowsMap[tableMeta.tableName] || [];
      const seenCompositeKey = new Map();

      rows.forEach((row, rowIndex) => {
        const missingPrimaryKeys = tableMeta.primaryKeys.filter(
          (primaryKey) => !hasValue(row[primaryKey]),
        );

        if (missingPrimaryKeys.length > 0) {
          errors.push(
            `${tableMeta.tableName} ${rowIndex + 1}행: PK(${missingPrimaryKeys.join(", ")}) 값은 비워둘 수 없습니다.`,
          );
          return;
        }

        const compositeKey = tableMeta.primaryKeys
          .map((primaryKey) => normalizeIdKey(row[primaryKey]))
          .join("|");

        if (seenCompositeKey.has(compositeKey)) {
          const firstRowIndex = seenCompositeKey.get(compositeKey);
          errors.push(
            `${tableMeta.tableName} ${rowIndex + 1}행: PK(${tableMeta.primaryKeys.join(", ")})가 ${firstRowIndex + 1}행과 중복됩니다.`,
          );
          return;
        }

        seenCompositeKey.set(compositeKey, rowIndex);
      });
    });

    const prodPlanRows = tableRowsMap[PROD_PLAN_TABLE] || [];
    const prodPlanIdSet = new Set(
      prodPlanRows
        .map((row) => normalizeIdKey(row.prod_id))
        .filter((value) => value),
    );

    prodPlanRows.forEach((row, rowIndex) => {
      const rowLabel = `sim_prod_plan ${rowIndex + 1}행`;
      const startType = parseIntegerSafe(row.start_type ?? 0);
      const parsedStartTime = parseDateSafe(row.start_time);
      const parsedDueTime = parseDateSafe(row.due_time);

      if (!hasValue(row.item_id)) {
        errors.push(`${rowLabel}: item_id는 비워둘 수 없습니다.`);
      }

      if (startType !== 0 && startType !== 1) {
        errors.push(`${rowLabel}: start_type은 0 또는 1이어야 합니다.`);
      }

      if (hasValue(row.start_time) && !parsedStartTime) {
        errors.push(`${rowLabel}: start_time 날짜 형식이 올바르지 않습니다.`);
      }

      if (hasValue(row.due_time) && !parsedDueTime) {
        errors.push(`${rowLabel}: due_time 날짜 형식이 올바르지 않습니다.`);
      }

      if (
        startType === 0 &&
        parsedStartTime &&
        parsedDueTime &&
        parsedDueTime.getTime() < parsedStartTime.getTime()
      ) {
        errors.push(`${rowLabel}: due_time은 start_time보다 빠를 수 없습니다.`);
      }

      if (startType === 1) {
        const startOrderId = parseIntegerSafe(row.start_order_id);
        const startCount = parseIntegerSafe(row.start_count);
        const currentProdId = normalizeIdKey(row.prod_id);
        const startOrderKey = normalizeIdKey(startOrderId);

        if (!startOrderId || startOrderId <= 0) {
          errors.push(
            `${rowLabel}: start_type=1이면 start_order_id는 1 이상의 정수여야 합니다.`,
          );
        } else {
          if (startOrderKey === currentProdId) {
            errors.push(
              `${rowLabel}: start_order_id는 자기 자신의 prod_id와 같을 수 없습니다.`,
            );
          }

          if (!prodPlanIdSet.has(startOrderKey)) {
            errors.push(
              `${rowLabel}: start_order_id(${startOrderId})에 해당하는 prod_id가 없습니다.`,
            );
          }
        }

        if (startCount === null || (startCount !== -1 && startCount < 1)) {
          errors.push(
            `${rowLabel}: start_type=1이면 start_count는 -1 또는 1 이상의 정수여야 합니다.`,
          );
        }
      }
    });

    return errors;
  }, [schemaTables, tableRowsMap]);

  const validateBeforeSubmit = useCallback(() => {
    const validationErrors = validateChanges();
    if (validationErrors.length === 0) {
      setErrorMessage("");
      return true;
    }

    const previewErrors = validationErrors.slice(0, 4).join(" | ");
    const suffix =
      validationErrors.length > 4
        ? ` | 외 ${validationErrors.length - 4}건`
        : "";

    setErrorMessage(`입력값 검증 실패: ${previewErrors}${suffix}`);
    return false;
  }, [validateChanges]);

  const appendExecutionLog = useCallback((message) => {
    const logText = String(message ?? "").trim();
    if (!logText) {
      return;
    }

    setExecutionLogs((prev) => {
      const next = [...prev, logText];
      if (next.length <= MAX_EXECUTION_LOG_LINES) {
        return next;
      }
      return next.slice(next.length - MAX_EXECUTION_LOG_LINES);
    });
  }, []);

  const scrollExecutionLogToLatest = useCallback((behavior = "auto") => {
    const logContainer = executionLogContainerRef.current;
    if (!logContainer) {
      return;
    }

    logContainer.scrollTo({
      top: logContainer.scrollHeight,
      behavior,
    });
  }, []);

  const handleExecutionLogScroll = useCallback(() => {
    const logContainer = executionLogContainerRef.current;
    if (!logContainer) {
      return;
    }

    const remainingScrollDistance =
      logContainer.scrollHeight -
      (logContainer.scrollTop + logContainer.clientHeight);

    setIsExecutionLogAutoFollow(remainingScrollDistance <= 12);
  }, []);

  const closeSimulationStream = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  const openSimulationStream = useCallback(
    (layoutId, streamToken) => {
      closeSimulationStream();

      if (!streamToken) {
        throw new Error("시뮬레이션 로그 스트림 토큰이 없습니다.");
      }

      if (!groupCode || !projectCode) {
        throw new Error(
          "그룹/프로젝트 정보가 없어 로그 스트림을 시작할 수 없습니다.",
        );
      }

      const streamUrl =
        `${rootUrl}/StreamingAssets/${groupCode}/${projectCode}/sv/Api/SimulationManagerStream` +
        `?layoutId=${encodeURIComponent(layoutId)}` +
        `&streamToken=${encodeURIComponent(streamToken)}`;

      const evtSource = new EventSource(streamUrl);
      eventSourceRef.current = evtSource;

      evtSource.onopen = () => {
        setExecutionStatus("running");
        setExecutionStatusMessage(
          `시뮬레이션 실행 중... (layoutId: ${layoutId})`,
        );
        appendExecutionLog(`SSE 연결 시작: ${streamUrl}`);
      };

      evtSource.onmessage = (event) => {
        const messages = String(event?.data ?? "").split("\n");

        messages.forEach((rawMessage) => {
          const trimmedMessage = rawMessage.trim();
          if (!trimmedMessage) {
            return;
          }

          try {
            const jsonData = JSON.parse(trimmedMessage);
            const statusCode = parseSimulationStatusCode(jsonData);
            const statusText = String(jsonData?.status ?? "");
            const messageText =
              typeof jsonData?.msg === "string" ||
              typeof jsonData?.msg === "number"
                ? String(jsonData.msg)
                : JSON.stringify(jsonData);

            appendExecutionLog(messageText);

            if (hasCancelKeyword(`${statusText} ${messageText}`)) {
              manualStopRef.current = true;
              setExecutionStatus("cancelled");
              setExecutionStatusMessage(
                "시뮬레이션 취소 메세지를 수신했습니다.",
              );
              closeSimulationStream();
              return;
            }

            if (statusCode === 9) {
              completionDetectedRef.current = true;
              setExecutionStatus("completed");
              setExecutionStatusMessage("Status=9 완료 메세지를 수신했습니다.");
              closeSimulationStream();
              return;
            }
          } catch {
            const fallbackMessage = `Ev:${trimmedMessage}`;
            appendExecutionLog(fallbackMessage);
          }
        });
      };

      evtSource.onerror = () => {
        closeSimulationStream();

        if (manualStopRef.current) {
          setExecutionStatus("cancelled");
          setExecutionStatusMessage("시뮬레이션이 취소되었습니다.");
          appendExecutionLog("SSE 연결 종료: 취소 처리 완료");
          return;
        }

        if (completionDetectedRef.current) {
          setExecutionStatus("completed");
          setExecutionStatusMessage("시뮬레이션이 완료되었습니다.");
          appendExecutionLog("SSE 연결 종료: 완료 처리");
          return;
        }

        setExecutionStatus("error");
        setExecutionStatusMessage(
          "시뮬레이션 로그 스트림 연결이 종료되었습니다.",
        );
        appendExecutionLog("SSE 연결 오류 또는 종료");
      };
    },
    [
      appendExecutionLog,
      closeSimulationStream,
      groupCode,
      projectCode,
      rootUrl,
    ],
  );

  const startSimulationManager = useCallback(
    async (layoutId) => {
      if (!groupCode || !projectCode) {
        throw new Error(
          "그룹/프로젝트 정보가 없어 시뮬레이션 시작 요청을 보낼 수 없습니다.",
        );
      }

      const response = await axiosPrivate.post(
        `/StreamingAssets/${groupCode}/${projectCode}/sv/Api/SimulationManagerStart`,
        { layoutId },
        { baseURL: rootUrl },
      );

      const payload = response?.data || {};
      const streamToken =
        typeof payload.streamToken === "string" ? payload.streamToken : "";
      if (!streamToken) {
        throw new Error("시뮬레이션 스트림 토큰을 받지 못했습니다.");
      }

      appendExecutionLog(
        `StartMgr: ${JSON.stringify(payload.upstream?.body || payload.message || "started")}`,
      );

      return streamToken;
    },
    [appendExecutionLog, groupCode, projectCode, rootUrl],
  );

  const stopSimulationExecution = useCallback(async () => {
    if (!["starting", "running", "cancelling"].includes(executionStatus)) {
      return;
    }

    manualStopRef.current = true;
    completionDetectedRef.current = false;
    setExecutionStatus("cancelling");
    setExecutionStatusMessage("시뮬레이션 취소 요청 중입니다...");
    appendExecutionLog(`StopMgr 요청 시작 (layoutId: ${activeLayoutId})`);

    try {
      if (!groupCode || !projectCode) {
        throw new Error(
          "그룹/프로젝트 정보가 없어 시뮬레이션 취소 요청을 보낼 수 없습니다.",
        );
      }

      const response = await axiosPrivate.post(
        `/StreamingAssets/${groupCode}/${projectCode}/sv/Api/SimulationManagerStop`,
        { layoutId: activeLayoutId },
        { baseURL: rootUrl },
      );

      const stopResultText = JSON.stringify(
        response?.data?.upstream?.body || response?.data?.message || "stopped",
      );

      appendExecutionLog(`StopMgr: ${stopResultText}`);
      setExecutionStatus("cancelled");
      setExecutionStatusMessage("시뮬레이션이 취소되었습니다.");
    } catch (error) {
      const stopErrorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "시뮬레이션 취소 요청 중 오류가 발생했습니다.";

      setExecutionStatus("error");
      setExecutionStatusMessage(stopErrorMessage);
      appendExecutionLog(stopErrorMessage);
    } finally {
      closeSimulationStream();
    }
  }, [
    activeLayoutId,
    appendExecutionLog,
    closeSimulationStream,
    executionStatus,
    groupCode,
    projectCode,
    rootUrl,
  ]);

  const loadSchema = useCallback(async () => {
    if (!groupCode || !projectCode) {
      return;
    }

    setSchemaLoading(true);
    setErrorMessage("");
    setSchemaTables([]);
    setSelectedDomainId("");
    setSelectedTableName("");
    setSelectedRowKeyByTable({});
    setTableRowsMap({});
    setInitialRowsMap({});
    setPendingDeleteRowsByTable({});

    try {
      const response = await axiosPrivate.get(
        `/StreamingAssets/${groupCode}/${projectCode}/sv/Api/ParameterSchema`,
        { baseURL: rootUrl },
      );

      const tables = Array.isArray(response.data?.tables)
        ? response.data.tables
        : [];
      setSchemaTables(tables);

      if (tables.length > 0) {
        const preferredPlanTable = tables.find(
          (table) =>
            inferDomainIdByTable(table.tableName, table.tableDesc) === "plan",
        );
        const targetTable = preferredPlanTable || tables[0];

        setSelectedTableName(targetTable.tableName);
        setSelectedDomainId(
          inferDomainIdByTable(targetTable.tableName, targetTable.tableDesc),
        );
      }
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          "시뮬레이션 파라미터 스키마를 불러오지 못했습니다.",
      );
    } finally {
      setSchemaLoading(false);
    }
  }, [groupCode, projectCode, rootUrl]);

  const loadTableRows = useCallback(
    async (tableName, force = false) => {
      if (!tableName || !groupCode || !projectCode) {
        return;
      }

      if (!force && tableRowsMap[tableName]) {
        return;
      }

      setLoadingTableName(tableName);
      setErrorMessage("");

      try {
        const response = await axiosPrivate.get(
          `/StreamingAssets/${groupCode}/${projectCode}/sv/Api/ParameterRows/${tableName}`,
          { baseURL: rootUrl },
        );

        const fetchedRows = Array.isArray(response.data?.rows)
          ? response.data.rows.map((row) => {
              const nextRow = { ...row };
              if (tableName === PROD_PLAN_TABLE) {
                const mappedItemName = itemNameById.get(
                  normalizeIdKey(row.item_id),
                );
                if (mappedItemName) {
                  nextRow.item_name = mappedItemName;
                }
              }
              return nextRow;
            })
          : [];

        setTableRowsMap((prev) => ({
          ...prev,
          [tableName]: fetchedRows,
        }));

        setInitialRowsMap((prev) => ({
          ...prev,
          [tableName]: fetchedRows.map((row) => ({ ...row })),
        }));

        setPendingDeleteRowsByTable((prev) => ({
          ...prev,
          [tableName]: [],
        }));

        setSelectedRowKeyByTable((prev) => {
          if (prev[tableName]) {
            return prev;
          }

          if (fetchedRows.length === 0) {
            return prev;
          }

          const firstKey = makeRowKey(fetchedRows[0], tableName, 0);
          return {
            ...prev,
            [tableName]: firstKey,
          };
        });
      } catch (error) {
        setErrorMessage(
          error?.response?.data?.message ||
            `${tableName} 파라미터 데이터를 불러오지 못했습니다.`,
        );
      } finally {
        setLoadingTableName("");
      }
    },
    [groupCode, itemNameById, projectCode, rootUrl, tableRowsMap],
  );

  const applyChangesToDatabase = useCallback(
    async (changes) => {
      if (!Array.isArray(changes) || changes.length === 0) {
        return;
      }

      await axiosPrivate.post(
        `/StreamingAssets/${groupCode}/${projectCode}/sv/Api/ParameterValues`,
        { changes },
        { baseURL: rootUrl },
      );

      const changedTableNames = [
        ...new Set(changes.map((change) => change.table).filter(Boolean)),
      ];

      for (const tableName of changedTableNames) {
        await loadTableRows(tableName, true);
      }
    },
    [groupCode, loadTableRows, projectCode, rootUrl],
  );

  const saveParameterChanges = useCallback(async () => {
    if (
      !groupCode ||
      !projectCode ||
      saving ||
      submitting ||
      isExecutionInProgress
    ) {
      return;
    }

    if (!validateBeforeSubmit()) {
      return;
    }

    const changes = buildChangesPayload();
    if (changes.length === 0) {
      setErrorMessage("저장할 변경사항이 없습니다.");
      return;
    }

    setSaving(true);
    setErrorMessage("");

    try {
      await applyChangesToDatabase(changes);
      setIsReviewOpen(false);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          "시뮬레이션 파라미터 DB 저장 중 오류가 발생했습니다.",
      );
    } finally {
      setSaving(false);
    }
  }, [
    applyChangesToDatabase,
    buildChangesPayload,
    groupCode,
    isExecutionInProgress,
    projectCode,
    saving,
    submitting,
    validateBeforeSubmit,
  ]);

  const executeSimulation = useCallback(async () => {
    if (
      !groupCode ||
      !projectCode ||
      saving ||
      ["starting", "running", "cancelling"].includes(executionStatus)
    ) {
      return;
    }

    if (!validateBeforeSubmit()) {
      return;
    }

    manualStopRef.current = false;
    completionDetectedRef.current = false;
    closeSimulationStream();
    setIsExecutionLogAutoFollow(true);
    setExecutionLogs([]);
    setExecutionStatus("starting");
    setExecutionStatusMessage(
      `시뮬레이션 시작 요청 중... (layoutId: ${activeLayoutId})`,
    );
    appendExecutionLog(
      `시뮬레이션 시작 요청 준비 (layoutId: ${activeLayoutId})`,
    );

    setSubmitting(true);
    setErrorMessage("");

    try {
      const changes = buildChangesPayload();

      if (changes.length > 0) {
        await applyChangesToDatabase(changes);
      }

      const response = await axiosPrivate.post(
        `/StreamingAssets/${groupCode}/${projectCode}/sv/Api/SimulationExecute`,
        {
          simId,
          changes: [],
        },
        { baseURL: rootUrl },
      );

      if (onExecuteRequested) {
        onExecuteRequested(response.data);
      }

      const streamToken = await startSimulationManager(activeLayoutId);
      openSimulationStream(activeLayoutId, streamToken);

      setIsReviewOpen(false);
    } catch (error) {
      closeSimulationStream();
      setExecutionStatus("error");
      setExecutionStatusMessage(
        error?.response?.data?.message ||
          error?.message ||
          "시뮬레이션 실행 요청 중 오류가 발생했습니다.",
      );
      appendExecutionLog(
        error?.response?.data?.message ||
          error?.message ||
          "시뮬레이션 실행 요청 중 오류가 발생했습니다.",
      );
      setErrorMessage(
        error?.response?.data?.message ||
          "시뮬레이션 실행 요청 중 오류가 발생했습니다.",
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    activeLayoutId,
    appendExecutionLog,
    applyChangesToDatabase,
    buildChangesPayload,
    closeSimulationStream,
    executionStatus,
    groupCode,
    onExecuteRequested,
    openSimulationStream,
    projectCode,
    rootUrl,
    saving,
    simId,
    startSimulationManager,
    validateBeforeSubmit,
  ]);

  useEffect(() => {
    if (open) {
      return;
    }

    closeSimulationStream();
    manualStopRef.current = false;
    completionDetectedRef.current = false;
  }, [closeSimulationStream, open]);

  useEffect(() => {
    return () => {
      closeSimulationStream();
    };
  }, [closeSimulationStream]);

  useEffect(() => {
    if (!open) {
      return;
    }

    loadSchema();
    setTableSearchKeyword("");
    setRowSearchKeyword("");
    setSimId(DEFAULT_SIM_ID);
    setSaving(false);
    setSubmitting(false);
    setViewMode("guided");
    setShowAdvancedFields(false);
    setIsReviewOpen(false);
    manualStopRef.current = false;
    completionDetectedRef.current = false;
    setExecutionStatus("idle");
    setExecutionStatusMessage("");
    setIsExecutionLogAutoFollow(true);
    setExecutionLogs([]);
  }, [loadSchema, open]);

  useEffect(() => {
    if (!isExecutionLogAutoFollow || executionLogs.length === 0) {
      return;
    }

    scrollExecutionLogToLatest();
  }, [executionLogs, isExecutionLogAutoFollow, scrollExecutionLogToLatest]);

  useEffect(() => {
    if (!open || !selectedTableName) {
      return;
    }

    loadTableRows(selectedTableName);
  }, [loadTableRows, open, selectedTableName]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!schemaTables.some((table) => table.tableName === ITEM_MASTER_TABLE)) {
      return;
    }

    if (
      !Object.prototype.hasOwnProperty.call(tableRowsMap, ITEM_MASTER_TABLE)
    ) {
      loadTableRows(ITEM_MASTER_TABLE);
    }
  }, [loadTableRows, open, schemaTables, tableRowsMap]);

  useEffect(() => {
    if (!Object.prototype.hasOwnProperty.call(tableRowsMap, PROD_PLAN_TABLE)) {
      return;
    }

    if (itemNameById.size === 0) {
      return;
    }

    const currentRows = tableRowsMap[PROD_PLAN_TABLE] || [];
    let changed = false;

    const nextRows = currentRows.map((row) => {
      const expectedItemName =
        itemNameById.get(normalizeIdKey(row.item_id)) || "";
      if ((row.item_name || "") === expectedItemName) {
        return row;
      }
      changed = true;
      return {
        ...row,
        item_name: expectedItemName,
      };
    });

    if (!changed) {
      return;
    }

    setTableRowsMap((prev) => ({
      ...prev,
      [PROD_PLAN_TABLE]: nextRows,
    }));
  }, [itemNameById, tableRowsMap]);

  useEffect(() => {
    if (!selectedDomainId || domainGroups.length === 0) {
      return;
    }

    const domain = domainGroups.find((item) => item.id === selectedDomainId);
    if (!domain) {
      return;
    }

    const hasSelectedTable = domain.tables.some(
      (table) => table.tableName === selectedTableName,
    );
    if (!hasSelectedTable) {
      setSelectedTableName(domain.tables[0]?.tableName || "");
    }
  }, [domainGroups, selectedDomainId, selectedTableName]);

  useEffect(() => {
    if (!selectedTableName || selectedRows.length === 0) {
      return;
    }

    if (selectedRowKey) {
      const exists = selectedRows.some(
        (row, rowIndex) =>
          makeRowKey(row, selectedTableName, rowIndex) === selectedRowKey,
      );
      if (exists) {
        return;
      }
    }

    const firstKey = makeRowKey(selectedRows[0], selectedTableName, 0);
    setSelectedRowKeyByTable((prev) => ({
      ...prev,
      [selectedTableName]: firstKey,
    }));
  }, [selectedRowKey, selectedRows, selectedTableName]);

  useEffect(() => {
    if (!open || viewMode !== "guided" || !selectedTableName) {
      return;
    }

    const targetRowKey =
      selectedRowKey ||
      (selectedRows.length > 0
        ? makeRowKey(selectedRows[0], selectedTableName, 0)
        : "");

    if (!targetRowKey) {
      return;
    }

    const targetRowButton = rowOptionButtonRefMap.current.get(targetRowKey);
    const rowListContainer = rowListContainerRef.current;
    if (!targetRowButton || !rowListContainer) {
      return;
    }

    const selectedTop = targetRowButton.offsetTop;
    const selectedBottom = selectedTop + targetRowButton.offsetHeight;
    const viewportTop = rowListContainer.scrollTop;
    const viewportBottom = viewportTop + rowListContainer.clientHeight;

    if (selectedTop < viewportTop || selectedBottom > viewportBottom) {
      const targetTop = Math.max(
        selectedTop -
          rowListContainer.clientHeight / 2 +
          targetRowButton.offsetHeight / 2,
        0,
      );

      rowListContainer.scrollTo({
        top: targetTop,
        behavior: "smooth",
      });
    }
  }, [open, selectedRowKey, selectedRows, selectedTableName, viewMode]);

  const handleDomainChange = (domainId) => {
    setSelectedDomainId(domainId);

    const domain = domainGroups.find((item) => item.id === domainId);
    if (!domain) {
      return;
    }

    if (domain.tables.some((table) => table.tableName === selectedTableName)) {
      return;
    }

    const firstTable = domain.tables[0];
    if (firstTable) {
      setSelectedTableName(firstTable.tableName);
      setRowSearchKeyword("");
    }
  };

  const handleTableChange = (tableName) => {
    setSelectedTableName(tableName);
    setRowSearchKeyword("");
    setShowAdvancedFields(false);
  };

  const handleRowChange = (tableName, rowKey) => {
    setSelectedRowKeyByTable((prev) => ({
      ...prev,
      [tableName]: rowKey,
    }));
  };

  const handleAddProdPlanRow = () => {
    if (!selectedTableMeta || selectedTableMeta.tableName !== PROD_PLAN_TABLE) {
      return;
    }

    const currentRows = tableRowsMap[PROD_PLAN_TABLE] || [];
    const initialRows = initialRowsMap[PROD_PLAN_TABLE] || [];

    const maxProdId = [...currentRows, ...initialRows].reduce((acc, row) => {
      const parsed = Number(row?.prod_id);
      if (Number.isInteger(parsed) && parsed > acc) {
        return parsed;
      }
      return acc;
    }, 0);

    const nextProdId = maxProdId + 1;
    const firstItemOption = itemOptions[0] || null;
    const rowKey = `__new__prod_plan__${Date.now()}__${Math.random().toString(16).slice(2, 8)}`;

    const defaultStartTime = new Date();
    defaultStartTime.setHours(9, 0, 0, 0);
    const defaultDueTime = new Date();
    defaultDueTime.setDate(defaultDueTime.getDate() + 1);
    defaultDueTime.setHours(18, 0, 0, 0);

    const newRow = {
      __rowKey: rowKey,
      __isNew: true,
      prod_id: nextProdId,
      prod_name: `생산계획_${nextProdId}`,
      item_id: firstItemOption ? firstItemOption.itemId : "",
      item_name: firstItemOption ? firstItemOption.itemName : "",
      quantity: 1,
      start_time: defaultStartTime.toISOString(),
      due_time: defaultDueTime.toISOString(),
      status: 0,
      start_type: 0,
      start_order_id: 0,
      start_lot_id: 0,
      start_count: 0,
    };

    selectedTableMeta.columns.forEach((column) => {
      if (Object.prototype.hasOwnProperty.call(newRow, column.name)) {
        return;
      }

      const fallback =
        column.defaultValue === null || column.defaultValue === undefined
          ? ""
          : column.defaultValue;
      if (column.name === "status") {
        newRow[column.name] = 0;
        return;
      }
      if (column.name === "start_type") {
        newRow[column.name] = 0;
        return;
      }
      newRow[column.name] = fallback;
    });

    setTableRowsMap((prev) => ({
      ...prev,
      [PROD_PLAN_TABLE]: [...(prev[PROD_PLAN_TABLE] || []), newRow],
    }));

    setSelectedRowKeyByTable((prev) => ({
      ...prev,
      [PROD_PLAN_TABLE]: rowKey,
    }));
  };

  const handleDeleteSelectedProdPlanRow = () => {
    if (
      !selectedTableMeta ||
      selectedTableMeta.tableName !== PROD_PLAN_TABLE ||
      !selectedRow
    ) {
      return;
    }

    const rowKey = makeRowKey(selectedRow, PROD_PLAN_TABLE, selectedRowIndex);
    const isNewRow = isNewLocalRow(selectedRow);
    const deletedRowSnapshot = { ...selectedRow };

    setTableRowsMap((prev) => {
      const nextRows = (prev[PROD_PLAN_TABLE] || []).filter((row, rowIndex) => {
        const targetKey = makeRowKey(row, PROD_PLAN_TABLE, rowIndex);
        return targetKey !== rowKey;
      });

      return {
        ...prev,
        [PROD_PLAN_TABLE]: nextRows,
      };
    });

    if (!isNewRow) {
      setPendingDeleteRowsByTable((prev) => {
        const prevRows = prev[PROD_PLAN_TABLE] || [];
        const hasSameRow = prevRows.some(
          (row) =>
            normalizeIdKey(row.prod_id) ===
            normalizeIdKey(deletedRowSnapshot.prod_id),
        );

        if (hasSameRow) {
          return prev;
        }

        return {
          ...prev,
          [PROD_PLAN_TABLE]: [...prevRows, deletedRowSnapshot],
        };
      });
    }
  };

  const handleCellValueChange = (tableName, rowIndex, columnName, value) => {
    setTableRowsMap((prev) => {
      const next = { ...prev };
      const targetRows = [...(next[tableName] || [])];
      const targetRow = { ...(targetRows[rowIndex] || {}) };
      targetRow[columnName] = value;

      if (tableName === PROD_PLAN_TABLE && columnName === "item_id") {
        const mappedItemName = itemNameById.get(normalizeIdKey(value)) || "";
        targetRow.item_name = mappedItemName;
      }

      targetRows[rowIndex] = targetRow;
      next[tableName] = targetRows;
      return next;
    });
  };

  const handleResetField = (tableName, rowIndex, row, column) => {
    const rowKey = makeRowKey(row, tableName, rowIndex);
    const initialRowMap = getInitialRowMapByTable(tableName);
    const initialRow = initialRowMap.get(rowKey);
    const fallbackValue = column?.defaultValue ?? "";
    const initialValue = initialRow?.[column.name] ?? fallbackValue;
    handleCellValueChange(tableName, rowIndex, column.name, initialValue);
  };

  const handleRefreshCurrentTable = async () => {
    if (!selectedTableName) {
      return;
    }

    await loadTableRows(selectedTableName, true);
  };

  const handleClose = () => {
    if (submitting || saving) {
      return;
    }

    if (["starting", "running", "cancelling"].includes(executionStatus)) {
      const confirmed = window.confirm(
        "시뮬레이션이 실행 중입니다. 팝업을 닫으면 로그 수신만 중단됩니다. 계속할까요?",
      );
      if (!confirmed) {
        return;
      }
    }

    if (totalChangedCells > 0) {
      const confirmed = window.confirm(
        `저장되지 않은 변경 ${totalChangedCells}개가 있습니다. 닫으면 변경이 사라집니다. 계속할까요?`,
      );
      if (!confirmed) {
        return;
      }
    }

    closeSimulationStream();
    setExecutionStatus("idle");
    setExecutionStatusMessage("");
    setIsExecutionLogAutoFollow(true);
    setExecutionLogs([]);
    setIsReviewOpen(false);
    onClose();
  };

  const openReviewModal = () => {
    if (!validateBeforeSubmit()) {
      return;
    }
    setIsReviewOpen(true);
  };

  const isSingleRowUnsafe =
    selectedTableMeta?.isSingleRowConfig &&
    Array.isArray(selectedRows) &&
    selectedRows.length > 1;
  const isProdPlanSelected = selectedTableMeta?.tableName === PROD_PLAN_TABLE;
  const shouldShowExecutionPanel =
    executionStatus !== "idle" || executionLogs.length > 0;
  const executionStatusLabelMap = {
    idle: "대기",
    starting: "시작 요청",
    running: "실행 중",
    cancelling: "취소 중",
    cancelled: "취소됨",
    completed: "완료",
    error: "오류",
  };
  const executionStatusColorMap = {
    idle: "text-slate-300",
    starting: "text-indigo-200",
    running: "text-cyan-200",
    cancelling: "text-amber-200",
    cancelled: "text-amber-300",
    completed: "text-emerald-300",
    error: "text-red-300",
  };

  const renderFieldEditor = (column) => {
    if (!selectedTableMeta || selectedRowIndex < 0) {
      return null;
    }

    const currentValue = selectedRow?.[column.name] ?? "";
    const beforeValue = selectedInitialRow?.[column.name] ?? "";
    const changed =
      toComparableValue(currentValue) !== toComparableValue(beforeValue);
    const options = getFieldOptions(column);
    const isDateColumn = isDateType(column.type);
    const isIntegerColumn = isIntegerType(column.type);
    const isDueTimeColumn = isDateColumn && column.name === "due_time";
    const startTimeMin = isDueTimeColumn
      ? toDateTimeLocalInput(selectedRow?.start_time ?? "")
      : undefined;
    const unit = getUnitText(column.description || "");
    const isProdPlanTable = selectedTableMeta.tableName === PROD_PLAN_TABLE;
    const isProdPlanItemField = isProdPlanTable && column.name === "item_id";
    const isProdPlanStartOrderField =
      isProdPlanTable && column.name === "start_order_id";

    const startOrderOptions = isProdPlanStartOrderField
      ? (tableRowsMap[PROD_PLAN_TABLE] || [])
          .filter(
            (row) =>
              normalizeIdKey(row.prod_id) !==
              normalizeIdKey(selectedRow?.prod_id),
          )
          .map((row) => ({
            value: String(row.prod_id ?? ""),
            label: `${row.prod_name || `주문 ${row.prod_id}`} (${row.prod_id})`,
          }))
      : [];

    const effectiveOptions = isProdPlanItemField
      ? itemOptions.map((option) => ({
          value: String(option.itemId ?? ""),
          label: `${option.itemName || option.itemId} (${option.itemId})`,
        }))
      : startOrderOptions.length > 0
        ? startOrderOptions
        : options;

    let helperText = column.description || "설명 없음";
    if (isProdPlanTable && column.name === "status") {
      helperText = "0은 생산, 1은 skip입니다.";
    } else if (isProdPlanTable && column.name === "start_order_id") {
      helperText =
        "start_type=1일 때, 지정 주문(prod_id)이 start_count 생산량에 도달하면 이 주문 생산을 시작합니다.";
    } else if (isProdPlanTable && column.name === "start_count") {
      helperText =
        "1 이상은 지정 수량, -1은 참조 주문의 계획 수량 도달을 의미합니다.";
    }

    const inputValue = isDateColumn
      ? toDateTimeLocalInput(currentValue)
      : String(currentValue ?? "");

    return (
      <div
        key={`${selectedTableMeta.tableName}-${selectedRowIndex}-${column.name}`}
        className={`rounded-xl border p-3 transition-colors ${
          changed
            ? "border-amber-400/60 bg-amber-500/8 shadow-[0_0_12px_rgba(245,158,11,0.08)]"
            : "border-slate-800 bg-slate-900/50 hover:border-slate-700"
        }`}
      >
        <div className="mb-2 flex items-start justify-between gap-2">
          <div>
            <div className="text-xs font-black text-slate-200">
              {getFieldLabel(column)}
            </div>
            <div className="mt-0.5 text-[10px] text-slate-500">
              {column.name}
              {unit && (
                <span className="ml-2 text-slate-400">단위: {unit}</span>
              )}
            </div>
          </div>
          {changed && (
            <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-bold text-amber-300">
              변경
            </span>
          )}
        </div>

        {effectiveOptions.length > 0 ? (
          <div className="relative">
            <select
              value={String(currentValue ?? "")}
              onChange={(event) =>
                handleCellValueChange(
                  selectedTableMeta.tableName,
                  selectedRowIndex,
                  column.name,
                  event.target.value,
                )
              }
              className="w-full appearance-none rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-2 pr-7 text-xs text-slate-100 cursor-pointer transition-colors focus:border-indigo-500/50 focus:outline-none focus:ring-1 focus:ring-indigo-500/30"
            >
              <option
                style={{ backgroundColor: "#0f172a", color: "#f1f5f9" }}
                value=""
              >
                선택
              </option>
              {effectiveOptions.map((option) => (
                <option
                  key={`${column.name}-${option.value}`}
                  value={option.value}
                  style={{ backgroundColor: "#0f172a", color: "#f1f5f9" }}
                >
                  {option.label}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500">
              <svg
                width="10"
                height="6"
                viewBox="0 0 10 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 1L5 5L9 1"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        ) : (
          <input
            type={
              isDateColumn
                ? "datetime-local"
                : isNumericType(column.type)
                  ? "number"
                  : "text"
            }
            step={isDateColumn ? undefined : isIntegerColumn ? "1" : "any"}
            min={isDueTimeColumn && startTimeMin ? startTimeMin : undefined}
            value={inputValue}
            style={isDateColumn ? { colorScheme: "dark" } : undefined}
            onChange={(event) =>
              handleCellValueChange(
                selectedTableMeta.tableName,
                selectedRowIndex,
                column.name,
                isDateColumn
                  ? normalizeDateInput(event.target.value)
                  : event.target.value,
              )
            }
            className="w-full rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-2 text-xs text-slate-100 transition-colors focus:border-indigo-500/50 focus:outline-none focus:ring-1 focus:ring-indigo-500/30 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
        )}

        <div className="mt-2 flex items-center justify-between gap-2 text-[10px]">
          <div className="truncate text-slate-500" title={helperText}>
            {helperText}
          </div>
          <button
            type="button"
            onClick={() =>
              handleResetField(
                selectedTableMeta.tableName,
                selectedRowIndex,
                selectedRow,
                column,
              )
            }
            disabled={!changed}
            className="shrink-0 rounded border border-slate-700 px-1.5 py-0.5 text-slate-400 transition-all hover:border-slate-500 hover:bg-slate-800 hover:text-white active:scale-95 disabled:cursor-not-allowed disabled:opacity-20"
          >
            원복
          </button>
        </div>
      </div>
    );
  };

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[1600]">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
        onClick={submitting || saving || isReviewOpen ? undefined : handleClose}
      />

      <div className="relative mx-3 my-3 flex h-[calc(100vh-1.5rem)] w-[calc(100%-1.5rem)] overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 text-slate-100 shadow-[0_40px_100px_rgba(0,0,0,0.65)]">
        <aside className="flex h-full w-[340px] flex-col border-r border-slate-800 bg-slate-950/90">
          <div className="border-b border-slate-800 px-4 py-4">
            <h2 className="text-sm font-extrabold tracking-[0.08em] text-slate-200">
              SIMULATION SETUP
            </h2>
            <p className="mt-1 text-[11px] text-slate-400">
              도메인 단위로 파라미터를 조정하고 실행 전에 변경사항을 검토합니다.
            </p>

            <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
              <div className="rounded-lg border border-slate-800 bg-slate-900/70 px-2 py-2">
                <div className="text-slate-500">변경 셀</div>
                <div className="mt-1 font-black text-amber-300">
                  {totalChangedCells}
                </div>
              </div>
              <div className="rounded-lg border border-slate-800 bg-slate-900/70 px-2 py-2">
                <div className="text-slate-500">변경 테이블</div>
                <div className="mt-1 font-black text-indigo-300">
                  {totalChangedTables}
                </div>
              </div>
            </div>
          </div>

          <div className="border-b border-slate-800 px-4 py-3">
            <h3 className="mb-2 text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">
              도메인
            </h3>
            <div className="space-y-1.5">
              {domainGroups.map((domain) => {
                const changedCount = changedCellCountByDomain[domain.id] || 0;
                const active = selectedDomainId === domain.id;
                return (
                  <button
                    key={domain.id}
                    type="button"
                    onClick={() => handleDomainChange(domain.id)}
                    className={`w-full rounded-xl border px-3 py-2 text-left transition-all active:scale-[0.99] ${
                      active
                        ? "border-indigo-400/40 bg-indigo-500/10 shadow-[0_0_0_1px_rgba(99,102,241,0.12)]"
                        : "border-transparent bg-slate-900/70 hover:border-slate-700 hover:bg-slate-900"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs font-bold text-slate-200">
                        {domain.label}
                      </div>
                      {changedCount > 0 && (
                        <span className="rounded-full bg-amber-500/20 px-2 py-[2px] text-[10px] font-bold text-amber-300">
                          {changedCount}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 truncate text-[10px] text-slate-500">
                      {domain.description}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="border-b border-slate-800 px-4 py-3">
            <h3 className="mb-2 text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">
              테이블
            </h3>
            <input
              type="text"
              value={tableSearchKeyword}
              onChange={(event) => setTableSearchKeyword(event.target.value)}
              placeholder="테이블 검색"
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-100 placeholder:text-slate-500 transition-colors focus:border-indigo-500/50 focus:outline-none focus:ring-1 focus:ring-indigo-500/30"
            />
          </div>

          <div className="flex-1 overflow-y-auto px-2 py-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-700 [&::-webkit-scrollbar-thumb:hover]:bg-slate-600 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1">
            {schemaLoading && (
              <div className="px-3 py-4 text-xs text-slate-400">
                스키마 로딩 중...
              </div>
            )}

            {!schemaLoading &&
              selectedDomainTables.map((table) => {
                const changedCount =
                  changedCellCountByTable[table.tableName] || 0;
                const editableCount = table.columns.filter(
                  (column) => column.editable,
                ).length;
                const readonlyOnly = editableCount === 0;

                return (
                  <button
                    key={table.tableName}
                    type="button"
                    onClick={() => handleTableChange(table.tableName)}
                    className={`mb-1 w-full rounded-xl border px-3 py-2 text-left transition-all active:scale-[0.99] ${
                      selectedTableName === table.tableName
                        ? "border-indigo-400/40 bg-indigo-500/10 shadow-[0_0_0_1px_rgba(99,102,241,0.12)]"
                        : "border-transparent bg-slate-900/70 hover:border-slate-700 hover:bg-slate-900"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-[12px] font-semibold text-slate-200">
                        {table.tableDesc || table.tableName}
                      </span>
                      <div className="flex items-center gap-1">
                        {readonlyOnly && (
                          <span className="rounded bg-slate-700 px-1.5 py-[1px] text-[9px] text-slate-300">
                            READONLY
                          </span>
                        )}
                        {changedCount > 0 && (
                          <span className="rounded-full bg-amber-500/20 px-2 py-[2px] text-[10px] font-bold text-amber-300">
                            {changedCount}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="mt-1 truncate text-[10px] text-slate-500">
                      {table.tableName}
                    </p>
                  </button>
                );
              })}

            {!schemaLoading && selectedDomainTables.length === 0 && (
              <div className="px-3 py-6 text-xs text-slate-500">
                선택한 도메인에 테이블이 없습니다.
              </div>
            )}
          </div>
        </aside>

        <section className="flex h-full flex-1 flex-col">
          <div className="flex items-center justify-between gap-3 border-b border-slate-800 px-5 py-4">
            <div>
              <h3 className="text-base font-bold tracking-tight text-slate-100">
                {selectedTableMeta
                  ? `${selectedDomain?.label || ""} / ${selectedTableMeta.tableDesc || selectedTableMeta.tableName}`
                  : "파라미터 테이블"}
              </h3>
              <p className="mt-1 text-xs text-slate-400">
                {selectedTableMeta
                  ? selectedTableMeta.tableName
                  : "도메인과 테이블을 선택하세요."}
              </p>
              {tableGuide?.relationHint && (
                <p className="mt-1 text-[11px] text-indigo-300/90">
                  {tableGuide.relationHint}
                </p>
              )}

              {executionStatus !== "idle" && executionStatusMessage && (
                <p
                  className={`mt-1 text-[11px] font-semibold ${
                    executionStatusColorMap[executionStatus] || "text-slate-300"
                  }`}
                >
                  {executionStatusMessage}
                </p>
              )}
            </div>

            <div className="flex items-center gap-3">
              <label
                className="text-xs font-semibold text-slate-300"
                htmlFor="sim-id-select"
              >
                SimId
              </label>
              <div className="relative">
                <select
                  id="sim-id-select"
                  value={simId}
                  onChange={(event) => setSimId(Number(event.target.value))}
                  className="appearance-none rounded-lg border border-slate-700 bg-slate-900 px-2 py-1 pr-6 text-xs text-slate-100 cursor-pointer transition-colors focus:border-indigo-500/50 focus:outline-none focus:ring-1 focus:ring-indigo-500/30"
                >
                  {[1, 2, 3, 4, 5].map((id) => (
                    <option
                      key={id}
                      value={id}
                      style={{ backgroundColor: "#0f172a", color: "#f1f5f9" }}
                    >
                      {id}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-500">
                  <svg
                    width="8"
                    height="5"
                    viewBox="0 0 10 6"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1L5 5L9 1"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>

              <button
                type="button"
                onClick={() =>
                  setViewMode((prev) => (prev === "guided" ? "raw" : "guided"))
                }
                className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-300 transition-colors hover:border-slate-500 hover:text-white"
              >
                {viewMode === "guided" ? "원본 표 보기" : "가이드 보기"}
              </button>

              <button
                type="button"
                onClick={handleClose}
                disabled={submitting || saving}
                className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-300 transition-colors hover:border-slate-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                닫기
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 border-b border-slate-800 px-5 py-3">
            <input
              type="text"
              value={rowSearchKeyword}
              onChange={(event) => setRowSearchKeyword(event.target.value)}
              placeholder="행 검색 (이름, ID, 값)"
              className="w-[360px] rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-100 placeholder:text-slate-500 transition-colors focus:border-indigo-500/50 focus:outline-none focus:ring-1 focus:ring-indigo-500/30"
            />

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={saveParameterChanges}
                disabled={
                  !selectedTableMeta ||
                  loadingTableName === selectedTableName ||
                  submitting ||
                  saving ||
                  isExecutionInProgress ||
                  totalChangedCells === 0
                }
                className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-bold text-emerald-200 transition-colors hover:border-emerald-400 hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {saving ? "저장 중..." : "변경값 저장"}
              </button>

              <button
                type="button"
                onClick={() => setShowAdvancedFields((prev) => !prev)}
                className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  showAdvancedFields
                    ? "border-indigo-400/50 bg-indigo-500/10 text-indigo-200"
                    : "border-slate-700 text-slate-300 hover:border-slate-500 hover:text-white"
                }`}
              >
                고급값 {showAdvancedFields ? "숨기기" : "보기"}
              </button>

              <div className="rounded-lg border border-slate-800 bg-slate-900/70 px-3 py-1.5 text-xs font-semibold text-slate-300">
                변경 셀:{" "}
                <span className="text-amber-300">{totalChangedCells}</span>
              </div>
            </div>
          </div>

          {errorMessage && (
            <div className="mx-5 mt-3 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200">
              {errorMessage}
            </div>
          )}

          {isSingleRowUnsafe && (
            <div className="mx-5 mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
              PK가 없는 테이블에 2개 이상 행이 존재해 안전하게 수정할 수
              없습니다.
            </div>
          )}

          {dependencyHint && (
            <div className="mx-5 mt-3 rounded-lg border border-indigo-500/30 bg-indigo-500/10 px-3 py-2 text-xs text-indigo-200">
              {dependencyHint}
            </div>
          )}

          {shouldShowExecutionPanel && (
            <div className="mx-5 mt-3 rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div
                    className={`text-xs font-bold ${
                      executionStatusColorMap[executionStatus] ||
                      "text-slate-300"
                    }`}
                  >
                    실행 상태:{" "}
                    {executionStatusLabelMap[executionStatus] ||
                      executionStatus}
                  </div>
                  <div className="mt-0.5 text-[10px] text-slate-500">
                    layoutId: {activeLayoutId}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {isExecutionInProgress && (
                    <span className="inline-flex items-center gap-1 rounded border border-indigo-500/30 bg-indigo-500/10 px-2 py-1 text-[10px] font-semibold text-indigo-200">
                      <span className="h-2.5 w-2.5 animate-spin rounded-full border border-indigo-200 border-t-transparent" />
                      로딩 중
                    </span>
                  )}

                  {!isExecutionLogAutoFollow && executionLogs.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsExecutionLogAutoFollow(true);
                        scrollExecutionLogToLatest("smooth");
                      }}
                      className="rounded border border-slate-500/50 bg-slate-800/80 px-2.5 py-1 text-[11px] font-semibold text-slate-200 transition-colors hover:border-slate-400 hover:bg-slate-700"
                    >
                      최신 로그로 이동
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={stopSimulationExecution}
                    disabled={!isExecutionInProgress || submitting || saving}
                    className="rounded border border-red-500/40 bg-red-500/10 px-2.5 py-1 text-[11px] font-semibold text-red-200 transition-colors hover:border-red-400 hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    실행 취소
                  </button>
                </div>
              </div>

              <div
                ref={executionLogContainerRef}
                onScroll={handleExecutionLogScroll}
                className="mt-2 max-h-[150px] overflow-y-auto overflow-x-hidden rounded border border-slate-800 bg-slate-950/80 px-2 py-2 text-[11px] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-700 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1"
              >
                {executionLogs.length === 0 && (
                  <div className="text-slate-500">수신된 로그가 없습니다.</div>
                )}

                {executionLogs.map((logLine, logIndex) => (
                  <div
                    key={`execution-log-${logIndex}`}
                    className="font-mono leading-relaxed text-slate-300 whitespace-pre-wrap break-all"
                  >
                    {logLine}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="relative flex-1 overflow-auto px-5 py-4 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-700 [&::-webkit-scrollbar-thumb:hover]:bg-slate-600 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5">
            {isExecutionInProgress && (
              <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-slate-950/40 backdrop-blur-[1px]">
                <div className="rounded-xl border border-slate-700 bg-slate-900/85 px-5 py-4 text-center shadow-2xl">
                  <div className="mx-auto h-7 w-7 animate-spin rounded-full border-2 border-indigo-200 border-t-transparent" />
                  <div className="mt-2 text-xs font-bold text-indigo-200">
                    시뮬레이션 실행 중
                  </div>
                  <div className="mt-1 text-[11px] text-slate-400">
                    SSE 로그를 수신하고 있습니다.
                  </div>
                </div>
              </div>
            )}

            {!selectedTableMeta && !schemaLoading && (
              <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4 text-sm text-slate-400">
                왼쪽에서 도메인과 테이블을 선택해 주세요.
              </div>
            )}

            {selectedTableMeta &&
              loadingTableName === selectedTableMeta.tableName && (
                <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4 text-sm text-slate-400">
                  {selectedTableMeta.tableName} 데이터 로딩 중...
                </div>
              )}

            {selectedTableMeta &&
              loadingTableName !== selectedTableMeta.tableName &&
              viewMode === "guided" && (
                <div className="space-y-4">
                  <div className="rounded-xl border border-slate-800 bg-slate-900/40">
                    <div className="flex items-center justify-between gap-3 border-b border-slate-800 px-4 py-3">
                      <div>
                        <h4 className="text-xs font-black uppercase tracking-[0.12em] text-slate-300">
                          행 선택
                        </h4>
                        <p className="mt-1 text-[11px] text-slate-500">
                          조정할 대상 행을 먼저 선택하세요.
                        </p>
                      </div>

                      {isProdPlanSelected && (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={handleAddProdPlanRow}
                            className="rounded border border-emerald-500/50 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-200 transition-colors hover:border-emerald-400 hover:bg-emerald-500/20"
                          >
                            + 행 추가
                          </button>
                          <button
                            type="button"
                            onClick={handleDeleteSelectedProdPlanRow}
                            disabled={!selectedRow}
                            className="rounded border border-red-500/40 bg-red-500/10 px-2.5 py-1 text-[11px] font-semibold text-red-200 transition-colors hover:border-red-400 hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            선택 삭제
                          </button>
                        </div>
                      )}
                    </div>
                    <div
                      ref={rowListContainerRef}
                      className="max-h-[180px] overflow-y-auto p-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-700 [&::-webkit-scrollbar-thumb:hover]:bg-slate-600 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1"
                    >
                      {filteredRows.length === 0 && (
                        <div className="px-2 py-6 text-center text-xs text-slate-500">
                          표시할 행이 없습니다.
                        </div>
                      )}

                      {filteredRows.map(({ row, rowIndex }) => {
                        const rowKey = makeRowKey(
                          row,
                          selectedTableMeta.tableName,
                          rowIndex,
                        );
                        const active =
                          selectedRowKey === rowKey ||
                          (!selectedRowKey && rowIndex === 0);
                        const rowChangedCount = getChangedCellCountByRow(
                          selectedTableMeta,
                          row,
                          rowIndex,
                        );

                        return (
                          <button
                            key={rowKey}
                            type="button"
                            ref={(node) => setRowOptionButtonRef(rowKey, node)}
                            onClick={() =>
                              handleRowChange(
                                selectedTableMeta.tableName,
                                rowKey,
                              )
                            }
                            className={`mb-1 w-full rounded-lg border px-3 py-2 text-left transition-all active:scale-[0.99] ${
                              active
                                ? "border-indigo-400/40 bg-indigo-500/10 shadow-[0_0_0_1px_rgba(99,102,241,0.15)]"
                                : "border-transparent bg-slate-900/70 hover:border-slate-700 hover:bg-slate-900"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="truncate text-xs font-semibold text-slate-200">
                                {buildRowTitle(
                                  selectedTableMeta,
                                  row,
                                  rowIndex,
                                )}
                              </span>
                              {rowChangedCount > 0 && (
                                <span className="rounded-full bg-amber-500/20 px-2 py-[2px] text-[10px] font-bold text-amber-300">
                                  {rowChangedCount}
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-[10px] text-slate-500">
                              행 {rowIndex + 1}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {selectedRow && (
                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_300px]">
                      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                        <div className="mb-3 flex items-center justify-between gap-2">
                          <div>
                            <h4 className="text-sm font-black text-slate-200">
                              {tableGuide?.headline || "핵심 조정값"}
                            </h4>
                            <p className="mt-1 text-[11px] text-slate-500">
                              선택 행:{" "}
                              {buildRowTitle(
                                selectedTableMeta,
                                selectedRow,
                                selectedRowIndex,
                              )}
                            </p>
                          </div>
                        </div>

                        {coreEditableColumns.length === 0 && (
                          <div className="rounded-lg border border-slate-800 bg-slate-900/70 px-3 py-4 text-xs text-slate-500">
                            이 테이블은 조정 가능한 필드가 없습니다.
                          </div>
                        )}

                        {coreEditableColumns.length > 0 && (
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            {coreEditableColumns.map((column) =>
                              renderFieldEditor(column),
                            )}
                          </div>
                        )}

                        {advancedEditableColumns.length > 0 &&
                          showAdvancedFields && (
                            <>
                              <div className="my-4 border-t border-slate-800 pt-3 text-xs font-black uppercase tracking-[0.12em] text-slate-400">
                                고급 조정값
                              </div>
                              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                {advancedEditableColumns.map((column) =>
                                  renderFieldEditor(column),
                                )}
                              </div>
                            </>
                          )}
                      </div>

                      <aside className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                        <h4 className="text-xs font-black uppercase tracking-[0.12em] text-slate-300">
                          참조값 / 키 정보
                        </h4>
                        <p className="mt-1 text-[11px] text-slate-500">
                          PK와 읽기전용 컬럼을 함께 보여줍니다.
                        </p>

                        <div className="mt-3 space-y-2">
                          {visibleReadonlyColumns.length === 0 && (
                            <div className="rounded-lg border border-slate-800 bg-slate-900/70 px-3 py-3 text-xs text-slate-500">
                              표시할 읽기전용 컬럼이 없습니다.
                            </div>
                          )}

                          {visibleReadonlyColumns.map((column) => (
                            <div
                              key={`readonly-${column.name}`}
                              className="rounded-lg border border-slate-800 bg-slate-900/70 px-3 py-2"
                            >
                              <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                                {column.name}
                              </div>
                              <div className="mt-1 text-xs text-slate-200">
                                {String(selectedRow[column.name] ?? "-")}
                              </div>
                            </div>
                          ))}
                        </div>
                      </aside>
                    </div>
                  )}
                </div>
              )}

            {selectedTableMeta &&
              loadingTableName !== selectedTableMeta.tableName &&
              viewMode === "raw" && (
                <div className="overflow-auto rounded-xl border border-slate-800 bg-slate-900/50">
                  <table className="min-w-full border-collapse text-xs text-slate-200">
                    <thead className="sticky top-0 z-10 bg-slate-900">
                      <tr>
                        <th className="border-b border-slate-700 px-3 py-2 text-left text-[11px] font-bold text-slate-300">
                          #
                        </th>
                        {selectedTableMeta.columns.map((column) => (
                          <th
                            key={column.name}
                            className="border-b border-slate-700 px-3 py-2 text-left text-[11px] font-bold text-slate-300"
                            title={column.description || column.name}
                          >
                            <div className="flex items-center gap-1">
                              <span>{column.name}</span>
                              {!column.editable && (
                                <span className="rounded bg-slate-700 px-1 py-[1px] text-[9px] text-slate-300">
                                  RO
                                </span>
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.length === 0 && (
                        <tr>
                          <td
                            colSpan={
                              (selectedTableMeta.columns?.length || 0) + 1
                            }
                            className="px-3 py-6 text-center text-slate-500"
                          >
                            표시할 데이터가 없습니다.
                          </td>
                        </tr>
                      )}

                      {filteredRows.map(({ row, rowIndex }) => (
                        <tr
                          key={makeRowKey(
                            row,
                            selectedTableMeta.tableName,
                            rowIndex,
                          )}
                        >
                          <td className="border-b border-slate-800 px-3 py-2 align-top text-slate-400">
                            {rowIndex + 1}
                          </td>
                          {selectedTableMeta.columns.map((column) => {
                            const rowKey = makeRowKey(
                              row,
                              selectedTableMeta.tableName,
                              rowIndex,
                            );
                            const initialRow =
                              selectedInitialRows.find(
                                (candidateRow, candidateIndex) =>
                                  makeRowKey(
                                    candidateRow,
                                    selectedTableMeta.tableName,
                                    candidateIndex,
                                  ) === rowKey,
                              ) || {};
                            const currentValue = row[column.name] ?? "";
                            const beforeValue = initialRow[column.name] ?? "";
                            const changed =
                              column.editable &&
                              toComparableValue(currentValue) !==
                                toComparableValue(beforeValue);
                            const editable =
                              column.editable &&
                              !isSingleRowUnsafe &&
                              shouldShowFieldByDependency(
                                selectedTableMeta.tableName,
                                row,
                                column.name,
                              );
                            const options = getFieldOptions(column);
                            const isProdPlanTable =
                              selectedTableMeta.tableName === PROD_PLAN_TABLE;
                            const isProdPlanItemField =
                              isProdPlanTable && column.name === "item_id";
                            const isProdPlanStartOrderField =
                              isProdPlanTable &&
                              column.name === "start_order_id";
                            const startOrderOptions = isProdPlanStartOrderField
                              ? (tableRowsMap[PROD_PLAN_TABLE] || [])
                                  .filter(
                                    (candidateRow) =>
                                      normalizeIdKey(candidateRow.prod_id) !==
                                      normalizeIdKey(row.prod_id),
                                  )
                                  .map((candidateRow) => ({
                                    value: String(candidateRow.prod_id ?? ""),
                                    label: `${candidateRow.prod_name || `주문 ${candidateRow.prod_id}`} (${candidateRow.prod_id})`,
                                  }))
                              : [];
                            const effectiveOptions = isProdPlanItemField
                              ? itemOptions.map((option) => ({
                                  value: String(option.itemId ?? ""),
                                  label: `${option.itemName || option.itemId} (${option.itemId})`,
                                }))
                              : startOrderOptions.length > 0
                                ? startOrderOptions
                                : options;
                            const isDateColumn = isDateType(column.type);
                            const isDueTimeColumn =
                              isDateColumn && column.name === "due_time";
                            const startTimeMin = isDueTimeColumn
                              ? toDateTimeLocalInput(row.start_time ?? "")
                              : undefined;
                            const valueForInput = isDateColumn
                              ? toDateTimeLocalInput(currentValue)
                              : String(currentValue ?? "");

                            return (
                              <td
                                key={`${selectedTableMeta.tableName}-${rowIndex}-${column.name}`}
                                className="border-b border-slate-800 px-3 py-2 align-top"
                              >
                                {editable ? (
                                  effectiveOptions.length > 0 ? (
                                    <div className="relative">
                                      <select
                                        value={String(currentValue ?? "")}
                                        onChange={(event) =>
                                          handleCellValueChange(
                                            selectedTableMeta.tableName,
                                            rowIndex,
                                            column.name,
                                            event.target.value,
                                          )
                                        }
                                        className={`w-full appearance-none rounded border bg-slate-900 px-2 py-1 pr-6 text-xs text-slate-100 cursor-pointer focus:outline-none focus:ring-1 focus:ring-indigo-500/30 ${
                                          changed
                                            ? "border-amber-400/80 bg-amber-500/10"
                                            : "border-slate-700"
                                        }`}
                                      >
                                        <option
                                          style={{
                                            backgroundColor: "#0f172a",
                                            color: "#f1f5f9",
                                          }}
                                          value=""
                                        >
                                          선택
                                        </option>
                                        {effectiveOptions.map((option) => (
                                          <option
                                            key={`${column.name}-${option.value}`}
                                            value={option.value}
                                            style={{
                                              backgroundColor: "#0f172a",
                                              color: "#f1f5f9",
                                            }}
                                          >
                                            {option.label}
                                          </option>
                                        ))}
                                      </select>
                                      <div className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-500">
                                        <svg
                                          width="8"
                                          height="5"
                                          viewBox="0 0 10 6"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M1 1L5 5L9 1"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      </div>
                                    </div>
                                  ) : (
                                    <input
                                      type={
                                        isDateColumn
                                          ? "datetime-local"
                                          : isNumericType(column.type)
                                            ? "number"
                                            : "text"
                                      }
                                      step={
                                        isDateColumn
                                          ? undefined
                                          : isIntegerType(column.type)
                                            ? "1"
                                            : "any"
                                      }
                                      min={
                                        isDueTimeColumn && startTimeMin
                                          ? startTimeMin
                                          : undefined
                                      }
                                      value={valueForInput}
                                      style={
                                        isDateColumn
                                          ? { colorScheme: "dark" }
                                          : undefined
                                      }
                                      onChange={(event) =>
                                        handleCellValueChange(
                                          selectedTableMeta.tableName,
                                          rowIndex,
                                          column.name,
                                          isDateColumn
                                            ? normalizeDateInput(
                                                event.target.value,
                                              )
                                            : event.target.value,
                                        )
                                      }
                                      className={`w-full rounded border bg-slate-900 px-2 py-1 text-xs text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500/30 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
                                        changed
                                          ? "border-amber-400/80 bg-amber-500/10"
                                          : "border-slate-700"
                                      }`}
                                    />
                                  )
                                ) : (
                                  <div
                                    className="min-h-[28px] rounded border border-slate-800 bg-slate-950/80 px-2 py-1 text-xs text-slate-400"
                                    title={column.reason || "읽기 전용"}
                                  >
                                    {String(currentValue ?? "")}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </div>

          <div className="flex items-center justify-between border-t border-slate-800 bg-slate-950/60 px-5 py-3.5 backdrop-blur-sm">
            <button
              type="button"
              onClick={handleRefreshCurrentTable}
              disabled={
                !selectedTableMeta ||
                loadingTableName === selectedTableName ||
                submitting ||
                saving ||
                isExecutionInProgress
              }
              className="flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-400 transition-all hover:border-slate-500 hover:bg-slate-800/80 hover:text-slate-200 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="shrink-0"
              >
                <path
                  d="M10.5 6a4.5 4.5 0 1 1-.9-2.7"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                />
                <path
                  d="M10.5 2.5V5.5H7.5"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              현재 테이블 새로고침
            </button>

            <button
              type="button"
              onClick={openReviewModal}
              disabled={
                !selectedTableMeta ||
                submitting ||
                saving ||
                isExecutionInProgress
              }
              className={`flex items-center gap-2 rounded-lg px-5 py-2 text-xs font-bold text-white transition-all active:scale-[0.97] disabled:cursor-not-allowed disabled:bg-slate-600 disabled:shadow-none ${
                totalChangedCells > 0
                  ? "bg-indigo-500 shadow-[0_0_16px_rgba(99,102,241,0.35)] hover:bg-indigo-400 hover:shadow-[0_0_22px_rgba(99,102,241,0.45)]"
                  : "bg-indigo-600/70 hover:bg-indigo-500"
              }`}
            >
              시뮬레이션 실행
              {totalChangedCells > 0 && (
                <span className="rounded-full bg-white/20 px-1.5 py-0.5 text-[10px] font-black tabular-nums">
                  {totalChangedCells}
                </span>
              )}
            </button>
          </div>
        </section>
      </div>

      {isReviewOpen && (
        <div className="absolute inset-0 z-[1700] flex items-center justify-center bg-black/60 px-4">
          <div className="max-h-[85vh] w-full max-w-4xl overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
            <div className="border-b border-slate-800 px-5 py-4">
              <h3 className="text-base font-black text-slate-100">
                실행 전 변경사항 검토
              </h3>
              <p className="mt-1 text-xs text-slate-400">
                실행 요청 전에 어떤 파라미터가 바뀌는지 확인하세요.
              </p>
              <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
                <div className="rounded-lg border border-slate-800 bg-slate-900/70 px-3 py-2">
                  <div className="text-slate-500">변경 셀</div>
                  <div className="mt-1 font-black text-amber-300">
                    {totalChangedCells}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-900/70 px-3 py-2">
                  <div className="text-slate-500">변경 테이블</div>
                  <div className="mt-1 font-black text-indigo-300">
                    {totalChangedTables}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-900/70 px-3 py-2">
                  <div className="text-slate-500">SimId</div>
                  <div className="mt-1 font-black text-slate-200">{simId}</div>
                </div>
              </div>
            </div>

            <div className="grid max-h-[56vh] grid-cols-1 gap-4 overflow-auto px-5 py-4 lg:grid-cols-[280px_minmax(0,1fr)] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-700 [&::-webkit-scrollbar-thumb:hover]:bg-slate-600 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1">
              <aside className="space-y-2">
                <h4 className="text-xs font-black uppercase tracking-[0.12em] text-slate-400">
                  도메인별 요약
                </h4>
                {reviewByDomain.length === 0 && (
                  <div className="rounded-lg border border-slate-800 bg-slate-900/70 px-3 py-3 text-xs text-slate-500">
                    변경사항이 없습니다. 기본값으로 실행됩니다.
                  </div>
                )}
                {reviewByDomain.map((domain) => (
                  <div
                    key={`review-domain-${domain.id}`}
                    className="rounded-lg border border-slate-800 bg-slate-900/70 px-3 py-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-bold text-slate-200">
                        {domain.label}
                      </span>
                      <span className="rounded-full bg-amber-500/20 px-2 py-[2px] text-[10px] font-bold text-amber-300">
                        {domain.items.length}
                      </span>
                    </div>
                  </div>
                ))}
              </aside>

              <div className="space-y-2">
                <h4 className="text-xs font-black uppercase tracking-[0.12em] text-slate-400">
                  상세 변경값
                </h4>

                {reviewItems.length === 0 && (
                  <div className="rounded-lg border border-slate-800 bg-slate-900/70 px-3 py-3 text-xs text-slate-500">
                    변경된 값이 없습니다.
                  </div>
                )}

                {reviewItems.map((item, index) => (
                  <div
                    key={`review-item-${index}`}
                    className="rounded-lg border border-slate-800 bg-slate-900/70 px-3 py-2 text-xs"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-bold text-slate-200">
                        [{item.domainLabel}] {item.tableDesc || item.tableName}
                      </div>
                      <div className="text-[10px] text-slate-500">
                        {item.rowLabel}
                      </div>
                    </div>
                    <div className="mt-1 text-[11px] text-slate-400">
                      {item.columnLabel}{" "}
                      <span className="text-slate-500">
                        ({item.columnName})
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-[11px]">
                      <span className="rounded bg-slate-800 px-1.5 py-0.5 text-slate-400">
                        {item.beforeValue || "(빈값)"}
                      </span>
                      <span className="text-slate-500">→</span>
                      <span className="rounded bg-amber-500/20 px-1.5 py-0.5 font-semibold text-amber-300">
                        {item.currentValue || "(빈값)"}
                      </span>
                    </div>
                  </div>
                ))}

                {omittedReviewItemCount > 0 && (
                  <div className="rounded-lg border border-slate-800 bg-slate-900/70 px-3 py-2 text-xs text-slate-500">
                    상세 변경값이 많아 {MAX_CHANGE_PREVIEW_ITEMS}개만
                    표시했습니다. 나머지 {omittedReviewItemCount}개는 저장/실행
                    요청에 함께 반영됩니다.
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-slate-800 px-5 py-3">
              <button
                type="button"
                onClick={() => setIsReviewOpen(false)}
                disabled={submitting || saving}
                className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-300 transition-colors hover:border-slate-500 hover:text-white disabled:opacity-40"
              >
                계속 수정
              </button>

              <button
                type="button"
                onClick={saveParameterChanges}
                disabled={
                  submitting ||
                  saving ||
                  isExecutionInProgress ||
                  totalChangedCells === 0
                }
                className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3.5 py-2 text-xs font-bold text-emerald-200 transition-colors hover:border-emerald-400 hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:bg-slate-600 disabled:opacity-40"
              >
                {saving ? "저장 중..." : "변경값만 저장"}
              </button>

              <button
                type="button"
                onClick={executeSimulation}
                disabled={submitting || saving || isExecutionInProgress}
                className="rounded-lg bg-indigo-500 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-indigo-400 disabled:cursor-not-allowed disabled:bg-slate-600"
              >
                {submitting
                  ? "저장 후 실행 요청 중..."
                  : `${totalChangedCells > 0 ? `${totalChangedCells}개 변경값 저장 후 ` : ""}시뮬레이션 실행 요청`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
